import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, AlertTriangle, Check, CreditCard, Croissant, Eye, Flower2, ImagePlus, Loader2, Lock, MapPin, Palette, Plus, Shield, ShieldCheck, Sparkles, SpellCheck, Store, FileText, ExternalLink, UtensilsCrossed, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPost, uploadFile, BACKEND_URL } from "@/api/client";
import { toast } from "@/components/ui/sonner";
import { useProjectLocal } from "@/context/AppContext";
import { LoadingScreen, LogoMark, Money } from "@/components/project/Primitives";
import { PriceField } from "@/components/project/PriceField";
import { formatMoney, getCurrencySymbol, pricePrefixPattern, CURRENCY_OPTIONS, suggestCurrencyForCountry } from "@/lib/currency";
import { normalizeEnabledLanguages, STOREFRONT_LANGUAGES, suggestLanguageForCountry } from "@/lib/languages";
import {
  ONBOARDING_UI_LANGUAGES,
  OnboardingI18nContext,
  getOnboardingT,
  readStoredOnboardingUiLanguage,
  storeOnboardingUiLanguage,
  useOnboardingI18n,
} from "@/lib/onboardingI18n";
import { useCurrency } from "@/hooks/useCurrency";
import { HoursEditor } from "@/components/project/HoursEditor";
import { LegalDocumentUploads, MenuImportPanel } from "@/components/project/OnboardingUploads";
import { CustomDomainPanel } from "@/components/project/CustomDomainPanel";

const PLATFORM_APP_HOST = (process.env.REACT_APP_PLATFORM_APP_HOST || "buildhof.com")
  .replace(/^https?:\/\//, "")
  .replace(/\/$/, "")
  .toLowerCase();
import {
  FOUNDATION_PACKAGE_ID,
  FOUNDATION_PLAN,
  OnboardingPlanStep,
  PLAN_ADDONS,
  PLAN_ONE_TIME,
  CATERING_LEAD_TIME_OPTIONS,
  PlanAddonOffer,
  PlanChargesSummary,
  normalizeCateringLeadHours,
  applyPublicPricing,
  calculatePlanTotals,
  getPlanOneTime,
  normalizePlanAddons,
  normalizePlanOneTime,
  togglePlanSelection,
} from "@/components/project/OnboardingPlan";
import {
    DELIVERY_PLATFORM_SUGGESTIONS,
    createDeliveryPlatformLink,
    normalizeDeliveryPlatformLinks,
} from "@/lib/deliveryPlatforms";
import {
    defaultOperatingHours,
    normalizeOperatingHours,
    serializeOperatingHours,
} from "@/lib/operatingHours";
import {
  activeLegalDocuments,
  formatBusinessAddress,
  getLegalComplianceGaps,
} from "@/lib/legalDocuments";
import {
  DPA_META,
  PLATFORM_AGREEMENT_BUNDLE_VERSION_ID,
  PLATFORM_AGREEMENT_DOCS,
  SERVICE_AGREEMENT_META,
} from "@/lib/platformAgreements";

/** Stripe Connect UI status from the business record (real acct_* only). */
function resolveStripeConnectStatus(businessOrForm) {
  const accountId = String(businessOrForm?.stripe_account_id || "").trim();
  const explicit = String(businessOrForm?.stripe_connect_status || "").trim();
  if (!accountId || accountId.startsWith("MOCKED_") || !accountId.startsWith("acct_")) {
    return "not_started";
  }
  if (["pending", "action_required", "connected"].includes(explicit)) return explicit;
  return "pending";
}

function applyConnectStatusToForm(current, connect, business) {
  if (connect) {
    const status = connect.stripe_connect_status || resolveStripeConnectStatus(connect) || "not_started";
    return {
      ...current,
      stripe_account_id: connect.stripe_account_id || "",
      stripe_connect_status: status,
      stripe_connected: status === "connected",
      stripe_connect_email: connect.email || "",
      stripe_connect_charges_enabled: Boolean(connect.charges_enabled),
      stripe_connect_payouts_enabled: Boolean(connect.payouts_enabled),
    };
  }
  const status = resolveStripeConnectStatus(business || current);
  return {
    ...current,
    stripe_account_id: business?.stripe_account_id || current.stripe_account_id || "",
    stripe_connect_status: status,
    stripe_connected: status === "connected",
    stripe_connect_email: business?.stripe_connect_email || current.stripe_connect_email || "",
    stripe_connect_charges_enabled: Boolean(business?.stripe_connect_charges_enabled ?? current.stripe_connect_charges_enabled),
    stripe_connect_payouts_enabled: Boolean(business?.stripe_connect_payouts_enabled ?? current.stripe_connect_payouts_enabled),
  };
}

const stepOrder = ["business-type", "business-details", "plan", "brand", "theme", "location", "hours", "products", "delivery", "payments", "domain", "legal", "preview", "agreements", "subscription", "publish"];

const BUSINESS_TYPE_ICONS = {
  bakery: Croissant,
  florist: Flower2,
  restaurant: UtensilsCrossed,
  cafe: Store,
};

const RESERVATION_NOTICE_OPTIONS = [
  { value: 1, label: "1 hour" },
  { value: 2, label: "2 hours" },
  { value: 4, label: "4 hours" },
  { value: 12, label: "12 hours" },
  { value: 24, label: "24 hours" },
  { value: 48, label: "48 hours" },
];

function normalizeReservationNoticeHours(value, fallback = 2) {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours < 0) return fallback;
  const allowed = RESERVATION_NOTICE_OPTIONS.map((option) => option.value);
  return allowed.includes(hours) ? hours : fallback;
}

function normalizePartySize(value, fallback) {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 1) return fallback;
  return Math.round(size);
}
const totalSteps = stepOrder.length;

function stepLabelKey(stepId) {
  return `step.${stepId}`;
}

async function logPlatformAgreementView(documentId, source = "link") {
  try {
    await apiPost("/onboarding/agreements/view", { document_id: documentId, source });
  } catch {
    // Non-blocking evidence log — never block reading the document.
  }
}

function slugify(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "my-local-business";
}

function parseMenuLines(text) {
  const prefix = pricePrefixPattern();
  return text.split("\n").map((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    const priceMatch = trimmed.match(new RegExp(`${prefix}(\\d+(?:\\.\\d{1,2})?)`, "i"));
    const price = priceMatch ? Number(priceMatch[1]) : 8 + index;
    const name = trimmed.replace(new RegExp(`${prefix}\\d+(?:\\.\\d{1,2})?`, "i"), "").replace(/[-–—]\s*$/, "").trim();
    return { name: name || `Imported item ${index + 1}`, description: "", price, category: index % 2 ? "Mains" : "Featured" };
  }).filter(Boolean);
}

export function Onboarding() {
  const { step = "business-type" } = useParams();
  const navigate = useNavigate();
  const { data, loading, isAuthenticated, refresh } = useProjectLocal();
  const business = data?.business;
  const index = Math.max(0, stepOrder.indexOf(step));
  const progress = Math.round(((index + 1) / totalSteps) * 100);
  const [uiLang, setUiLangState] = useState(readStoredOnboardingUiLanguage);
  const t = useMemo(() => getOnboardingT(uiLang), [uiLang]);
  const setUiLang = (code) => setUiLangState(storeOnboardingUiLanguage(code));
  const i18nValue = useMemo(() => ({ lang: uiLang, setLang: setUiLang, t }), [uiLang, t]);
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [correctingCopy, setCorrectingCopy] = useState(false);
  const [payingSubscription, setPayingSubscription] = useState(false);
  const [previewMode, setPreviewMode] = useState("mobile");
  const [form, setForm] = useState(() => ({
    type_id: business?.type_id || "restaurant",
    name: business?.name || "My Local Restaurant",
    description: business?.description || "A welcoming local business ready to take online orders.",
    logo_url: business?.logo_url || "",
    photos_text: (business?.photos || ["https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&w=1400&q=80"]).join("\n"),
    theme_id: business?.theme_id || "theme_modern",
    primary_color: business?.theme_overrides?.primary_color || "#173B2F",
    accent_color: business?.theme_overrides?.accent_color || "#EA6A3B",
    background_color: business?.theme_overrides?.background_color || "#FFFDF8",
    text_color: business?.theme_overrides?.text_color || "#171717",
    heading_font: business?.theme_overrides?.heading_font || "Sora",
    body_font: business?.theme_overrides?.body_font || "DM Sans",
    address_line1: business?.address?.line1 || "18 Market Lane",
    address_line2: business?.address?.line2 || "",
    city: business?.address?.city || "Bristol",
    postcode: business?.address?.postcode || "BS1 5QD",
    country: business?.address?.country || "United Kingdom",
    currency: business?.currency || suggestCurrencyForCountry(business?.address?.country || "United Kingdom"),
    default_language: business?.default_language || suggestLanguageForCountry(business?.address?.country || "United Kingdom"),
    enabled_languages: normalizeEnabledLanguages(
      business?.enabled_languages,
      business?.default_language || suggestLanguageForCountry(business?.address?.country || "United Kingdom"),
    ),
    show_address: business?.address?.show_on_storefront ?? true,
    phone: business?.phone || "",
    email: business?.email || "",
    hours: normalizeOperatingHours(
        business?.operating_hours || defaultOperatingHours({ closedDay: "Mon", openFrom: "12:00", openTo: "21:30" })
    ),
    menu_text: "Charred peach burrata £9.50\nWild mushroom gnocchi £17\nBurnt honey tart £7",
    parsed_products: [],
    pickup_enabled: business?.pickup_enabled ?? true,
    delivery_enabled: business?.delivery_enabled ?? true,
    reservations_enabled: business?.reservations_enabled ?? false,
    reservation_party_min: String(normalizePartySize(business?.reservation_party_min, 2)),
    reservation_party_max: String(normalizePartySize(business?.reservation_party_max, 8)),
    reservation_min_notice_hours: normalizeReservationNoticeHours(business?.reservation_min_notice_hours, 2),
    delivery_radius: String(business?.delivery_radius || 4),
    delivery_radius_unit: business?.delivery_radius_unit === "km" ? "km" : "mi",
    delivery_fee: String(business?.delivery_fee || 3.5),
    delivery_time: business?.delivery_time || "35–45 min",
    pickup_instructions: business?.pickup_instructions || "Collect from the counter.",
    delivery_platform_links: normalizeDeliveryPlatformLinks(business?.delivery_platform_links),
    min_order_value: String(business?.min_order_value || 12),
    stripe_account_id: business?.stripe_account_id || "",
    stripe_connected: resolveStripeConnectStatus(business) === "connected",
    stripe_connect_status: resolveStripeConnectStatus(business),
    stripe_connect_email: business?.stripe_connect_email || "",
    stripe_connect_charges_enabled: Boolean(business?.stripe_connect_charges_enabled),
    stripe_connect_payouts_enabled: Boolean(business?.stripe_connect_payouts_enabled),
    slug: business?.slug || slugify(business?.name || "my-local-business"),
    desired_custom_domain: business?.desired_custom_domain || "",
    domain_ownership_confirmed: Boolean(business?.domain_ownership_confirmed),
    legal_documents: business?.legal_documents || {},
    hero_headline: business?.storefront_sections?.[0]?.fields?.headline || `Welcome to ${business?.name || "your store"}`,
    hero_subtext: business?.storefront_sections?.[0]?.fields?.subtext || "Order online for pickup or delivery.",
    about_text: business?.storefront_sections?.find((section) => section.id === "about")?.fields?.text || "A friendly local business built for easy online ordering.",
    subscription_package_id: business?.subscription_package_id || FOUNDATION_PACKAGE_ID,
    subscription_active: business?.subscription_status === "active",
    plan_addons: normalizePlanAddons(business?.subscription_addons),
    plan_one_time: normalizePlanOneTime(business?.subscription_one_time),
    catering_min_lead_hours: normalizeCateringLeadHours(business?.catering_min_lead_hours, 48),
    payment_mode: "stripe",
    legal_soft_ack: false,
    agreed_platform_agreements: Boolean(business?.platform_agreements?.accepted),
    marketing_consent: Boolean(business?.platform_agreements?.marketing_consent?.accepted),
  }));
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingConfig, setBillingConfig] = useState({
    stripe_configured: false,
    connect_enabled: false,
    default_mode: "stripe",
    test_mode: false,
    live_mode: false,
  });

  useEffect(() => {
    if (!business) return;
    setForm((current) => {
      const mediaPatch = {
        // Always refresh signed media URLs from bootstrap (signatures rotate).
        logo_url: business.logo_url != null && business.logo_url !== "" ? business.logo_url : current.logo_url,
        photos_text: Array.isArray(business.photos) && business.photos.length
          ? business.photos.join("\n")
          : current.photos_text,
        theme_id: business.theme_id || current.theme_id,
        primary_color: business.theme_overrides?.primary_color || current.primary_color,
        accent_color: business.theme_overrides?.accent_color || current.accent_color,
        background_color: business.theme_overrides?.background_color || current.background_color,
        text_color: business.theme_overrides?.text_color || current.text_color,
        heading_font: business.theme_overrides?.heading_font || current.heading_font,
        body_font: business.theme_overrides?.body_font || current.body_font,
      };
      if (current.name !== "My Local Restaurant") {
        return { ...current, ...mediaPatch };
      }
      return {
        ...current,
        ...mediaPatch,
        type_id: business.type_id || current.type_id,
        name: business.name || current.name,
        description: business.description || current.description,
        slug: business.slug || current.slug,
        hero_headline: business.storefront_sections?.[0]?.fields?.headline || current.hero_headline,
        hero_subtext: business.storefront_sections?.[0]?.fields?.subtext || current.hero_subtext,
        about_text: business.storefront_sections?.find((section) => section.id === "about")?.fields?.text || current.about_text,
        subscription_package_id: business.subscription_package_id || current.subscription_package_id,
        subscription_active: business.subscription_status === "active",
        plan_addons: normalizePlanAddons(business.subscription_addons || current.plan_addons),
        plan_one_time: normalizePlanOneTime(business.subscription_one_time || current.plan_one_time),
        catering_min_lead_hours: normalizeCateringLeadHours(
          business.catering_min_lead_hours ?? current.catering_min_lead_hours,
          48,
        ),
        stripe_account_id: business.stripe_account_id || current.stripe_account_id,
        stripe_connected: resolveStripeConnectStatus(business) === "connected",
        stripe_connect_status: resolveStripeConnectStatus(business),
        stripe_connect_email: business.stripe_connect_email || current.stripe_connect_email,
        stripe_connect_charges_enabled: Boolean(business.stripe_connect_charges_enabled),
        stripe_connect_payouts_enabled: Boolean(business.stripe_connect_payouts_enabled),
        currency: business.currency || current.currency,
        default_language: business.default_language || current.default_language,
        enabled_languages: normalizeEnabledLanguages(
          business.enabled_languages || current.enabled_languages,
          business.default_language || current.default_language,
        ),
        desired_custom_domain: business.desired_custom_domain || current.desired_custom_domain,
        domain_ownership_confirmed: Boolean(business.domain_ownership_confirmed ?? current.domain_ownership_confirmed),
        legal_documents: business.legal_documents || current.legal_documents,
        delivery_platform_links: normalizeDeliveryPlatformLinks(business.delivery_platform_links || current.delivery_platform_links),
        phone: business.phone || current.phone,
        email: business.email || current.email,
        delivery_radius: business.delivery_radius != null ? String(business.delivery_radius) : current.delivery_radius,
        delivery_radius_unit: business.delivery_radius_unit === "km" ? "km" : (business.delivery_radius_unit === "mi" ? "mi" : current.delivery_radius_unit),
        reservations_enabled: business.reservations_enabled ?? current.reservations_enabled,
        reservation_party_min: business.reservation_party_min != null
          ? String(normalizePartySize(business.reservation_party_min, 2))
          : current.reservation_party_min,
        reservation_party_max: business.reservation_party_max != null
          ? String(normalizePartySize(business.reservation_party_max, 8))
          : current.reservation_party_max,
        reservation_min_notice_hours: normalizeReservationNoticeHours(
          business.reservation_min_notice_hours ?? current.reservation_min_notice_hours,
          2,
        ),
        agreed_platform_agreements: Boolean(business.platform_agreements?.accepted ?? current.agreed_platform_agreements),
        marketing_consent: Boolean(business.platform_agreements?.marketing_consent?.accepted ?? current.marketing_consent),
      };
    });
  }, [business]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    apiGet("/billing/config")
      .then((config) => {
        applyPublicPricing(config.pricing);
        setBillingConfig(config);
        setForm((current) => ({ ...current, payment_mode: "stripe" }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get("paid") !== "1") return;
    if (form.subscription_active) {
      searchParams.delete("paid");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await apiPost("/onboarding/subscription/confirm", {
          session_id: searchParams.get("session_id") || undefined,
        });
        if (cancelled) return;
        setForm((current) => ({
          ...current,
          subscription_active: true,
          subscription_package_id: result.package?.id || result.business?.subscription_package_id || current.subscription_package_id,
          plan_addons: normalizePlanAddons(result.business?.subscription_addons || current.plan_addons),
          plan_one_time: normalizePlanOneTime(result.business?.subscription_one_time || current.plan_one_time),
        }));
        await refresh();
        toast.success("Stripe payment confirmed — you can go live");
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.detail?.error?.message || "Waiting for Stripe payment confirmation…");
        }
      } finally {
        if (!cancelled) {
          searchParams.delete("paid");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, form.subscription_active, refresh, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("canceled") !== "1") return;
    toast.error("Payment was canceled or failed. Complete Pay & go live before opening the dashboard or publishing.");
    searchParams.delete("canceled");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    if (step !== "publish") return;
    if (searchParams.get("paid") === "1") return;
    if (form.subscription_active || business?.subscription_status === "active") return;
    toast.error("Complete subscription payment before going live.");
    navigate("/onboarding/subscription", { replace: true });
  }, [loading, isAuthenticated, step, form.subscription_active, business?.subscription_status, navigate, searchParams]);

  const selectedConfig = useMemo(() => data?.business_types?.find((type) => type.type_id === form.type_id), [data?.business_types, form.type_id]);
  const previewPhotos = form.photos_text.split("\n").map((item) => item.trim()).filter(Boolean);
  const previewProducts = form.parsed_products.length ? form.parsed_products : parseMenuLines(form.menu_text).slice(0, 3);

  const saveStep = async (targetStep = step) => {
    const photos = previewPhotos;
    const payloads = {
      "business-type": { type_id: form.type_id },
      "business-details": { name: form.name, description: form.description, hero_headline: form.hero_headline, hero_subtext: form.hero_subtext, about_text: form.about_text },
      plan: {
        package_id: form.subscription_package_id || FOUNDATION_PACKAGE_ID,
      },
      brand: {
        logo_url: toStoredMediaUrl(form.logo_url),
        photos: photos.map(toStoredMediaUrl),
      },
      theme: {
        theme_id: form.theme_id,
        primary_color: form.primary_color,
        accent_color: form.accent_color,
        background_color: form.background_color,
        text_color: form.text_color,
        heading_font: form.heading_font,
        body_font: form.body_font,
        one_time: normalizePlanOneTime(form.plan_one_time),
      },
      location: {
        address: { line1: form.address_line1, line2: form.address_line2, city: form.city, postcode: form.postcode, country: form.country, show_on_storefront: form.show_address },
        currency: form.currency,
        default_language: form.default_language,
        enabled_languages: normalizeEnabledLanguages(form.enabled_languages, form.default_language),
        phone: form.phone,
        email: form.email,
      },
      hours: {
        operating_hours: serializeOperatingHours(form.hours),
      },
      products: {
        menu_text: form.menu_text,
        products: previewProducts,
        business_type: form.type_id,
        one_time: normalizePlanOneTime(form.plan_one_time),
      },
      delivery: {
        pickup_enabled: form.pickup_enabled,
        delivery_enabled: form.delivery_enabled,
        delivery_radius: Number(form.delivery_radius || 0),
        delivery_radius_unit: form.delivery_radius_unit === "km" ? "km" : "mi",
        delivery_fee: Number(form.delivery_fee || 0),
        delivery_time: form.delivery_time,
        pickup_instructions: form.pickup_instructions,
        min_order_value: Number(form.min_order_value || 0),
        delivery_platform_links: form.delivery_platform_links,
        reservations_enabled: Boolean(form.reservations_enabled),
        reservation_party_min: normalizePartySize(form.reservation_party_min, 2),
        reservation_party_max: Math.max(
          normalizePartySize(form.reservation_party_min, 2),
          normalizePartySize(form.reservation_party_max, 8),
        ),
        reservation_min_notice_hours: normalizeReservationNoticeHours(form.reservation_min_notice_hours, 2),
        addons: normalizePlanAddons(form.plan_addons),
        catering_min_lead_hours: normalizeCateringLeadHours(form.catering_min_lead_hours, 48),
      },
      payments: {},
      domain: {
        slug: form.slug,
        desired_custom_domain: form.desired_custom_domain,
        domain_ownership_confirmed: Boolean(form.domain_ownership_confirmed),
      },
      legal: { documents: form.legal_documents },
      preview: { hero_headline: form.hero_headline, hero_subtext: form.hero_subtext, about_text: form.about_text, operating_hours: serializeOperatingHours(form.hours), products: previewProducts, preview_mode: previewMode },
      agreements: {
        accepted_bundle: Boolean(form.agreed_platform_agreements),
        service_agreement: Boolean(form.agreed_platform_agreements),
        data_processing_agreement: Boolean(form.agreed_platform_agreements),
        marketing_consent: Boolean(form.marketing_consent),
        version_id: PLATFORM_AGREEMENT_BUNDLE_VERSION_ID,
        service_agreement_version: SERVICE_AGREEMENT_META.version,
        service_agreement_version_id: SERVICE_AGREEMENT_META.version_id,
        data_processing_agreement_version: DPA_META.version,
        data_processing_agreement_version_id: DPA_META.version_id,
      },
      subscription: {
        package_id: form.subscription_package_id || FOUNDATION_PACKAGE_ID,
        addons: normalizePlanAddons(form.plan_addons),
        one_time: normalizePlanOneTime(form.plan_one_time),
      },
      publish: { published: true, onboarding_completed: true },
    };
    if (isAuthenticated) {
      await apiPost("/onboarding/save", { step: targetStep, data: payloads[targetStep] || { completed: true } });
      if (targetStep === "domain") await refresh();
    }
    toast.success(isAuthenticated ? "Auto-saved to your storefront" : "Auto-saved in demo mode");
  };

  const paySubscription = async () => {
    if (!form.subscription_package_id) {
      toast.error("Choose a subscription package first.");
      return false;
    }
    setPayingSubscription(true);
    try {
      const result = await apiPost("/onboarding/subscription/pay", {
        package_id: form.subscription_package_id || FOUNDATION_PACKAGE_ID,
        addons: normalizePlanAddons(form.plan_addons),
        one_time: normalizePlanOneTime(form.plan_one_time),
        payment_mode: "stripe",
      });
      if (result.checkout_url) {
        toast.message("Redirecting to Stripe Checkout…");
        window.location.assign(result.checkout_url);
        return false;
      }
      setForm((current) => ({
        ...current,
        subscription_active: true,
        subscription_package_id: result.package?.id || current.subscription_package_id,
        plan_addons: normalizePlanAddons(result.business?.subscription_addons || current.plan_addons),
        plan_one_time: normalizePlanOneTime(result.business?.subscription_one_time || current.plan_one_time),
      }));
      await refresh();
      toast.success(result.already_active ? "Subscription already active" : "Payment complete");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail?.error?.message || "Subscription payment failed");
      return false;
    } finally {
      setPayingSubscription(false);
    }
  };

  const next = async () => {
    if (step === "domain" && String(form.desired_custom_domain || "").trim() && !form.domain_ownership_confirmed) {
      toast.error("Confirm you own or are authorized to manage this domain before continuing.");
      return;
    }
    if (step === "agreements") {
      if (!form.agreed_platform_agreements) {
        toast.error("Please accept the Service Agreement and Data Processing Agreement before continuing to payment.");
        return;
      }
    }
    if (step === "subscription" && !form.subscription_active) {
      const legalGaps = getLegalComplianceGaps({ legal_documents: form.legal_documents, country: form.country });
      if (legalGaps.impressumMissing) {
        toast.error("Add your Impressum before going live.");
        return;
      }
      if (legalGaps.softMissing.length && !form.legal_soft_ack) {
        toast.error("Confirm you understand the missing legal documents, or add them first.");
        return;
      }
      const paid = await paySubscription();
      if (!paid) return;
    }
    await saveStep(step);
    if (step === "location" || step === "domain" || step === "agreements" || step === "publish") await refresh();
    navigate(index === stepOrder.length - 1 ? "/dashboard" : `/onboarding/${stepOrder[index + 1]}`);
  };

  const skipStep = async () => {
    if (step === "subscription" || step === "publish" || step === "agreements") return;
    await saveStep(step);
    navigate(`/onboarding/${stepOrder[index + 1]}`);
  };

  const subscriptionPaid = Boolean(form.subscription_active || business?.subscription_status === "active");

  const skipToDashboard = (event) => {
    event.preventDefault();
    if (!subscriptionPaid) {
      toast.error("Complete payment to open the dashboard. Publishing stays locked until Pay & go live succeeds.");
      if (step !== "subscription") navigate("/onboarding/subscription");
      return;
    }
    navigate("/dashboard");
  };

  const back = () => navigate(index <= 0 ? "/signup" : `/onboarding/${stepOrder[index - 1]}`);

  const legalGaps = useMemo(
    () => getLegalComplianceGaps({ legal_documents: form.legal_documents, country: form.country }),
    [form.legal_documents, form.country]
  );
  const legalStepIndex = stepOrder.indexOf("legal");
  const legalChecklistWarning = index > legalStepIndex && legalGaps.hasAnyGap;
  const subscriptionBlockedByImpressum = step === "subscription" && !form.subscription_active && legalGaps.impressumMissing;
  const subscriptionBlockedBySoftWarn =
    step === "subscription"
    && !form.subscription_active
    && !legalGaps.impressumMissing
    && legalGaps.softMissing.length > 0
    && !form.legal_soft_ack;
  const agreementsIncomplete = step === "agreements" && !form.agreed_platform_agreements;
  const nextDisabled = payingSubscription || subscriptionBlockedByImpressum || subscriptionBlockedBySoftWarn || agreementsIncomplete;
  const generateDescription = async () => {
    setGeneratingCopy(true);
    try {
      const draftDescription = effectiveOnboardingDraft(form.description, ONBOARDING_PLACEHOLDER_DESCRIPTIONS);
      const draftHeroSubtext = effectiveOnboardingDraft(form.hero_subtext, ONBOARDING_PLACEHOLDER_SUBTEXTS);
      const result = await apiPost("/onboarding/ai/generate-copy", {
        business_name: form.name,
        business_type: form.type_id,
        business_type_label: selectedConfig?.display_name,
        draft_description: draftDescription,
        draft_hero_subtext: draftHeroSubtext,
      });
      setForm((current) => ({
        ...current,
        description: result.description,
        hero_subtext: result.hero_subtext,
      }));
      toast.success("Description generated with AI");
    } catch (error) {
      toast.error(error.response?.data?.detail?.error?.message || "AI generation failed");
    } finally {
      setGeneratingCopy(false);
    }
  };
  const correctSpelling = async () => {
    setCorrectingCopy(true);
    try {
      const result = await apiPost("/onboarding/ai/spell-correct", {
        description: form.description,
        hero_headline: form.hero_headline,
        hero_subtext: form.hero_subtext,
      });
      setForm((current) => ({
        ...current,
        ...(result.description ? { description: result.description } : {}),
        ...(result.hero_headline ? { hero_headline: result.hero_headline } : {}),
        ...(result.hero_subtext ? { hero_subtext: result.hero_subtext } : {}),
      }));
      toast.success("Spelling corrected with AI");
    } catch (error) {
      toast.error(error.response?.data?.detail?.error?.message || "Spell correction failed");
    } finally {
      setCorrectingCopy(false);
    }
  };
  const setHours = (hours) => setForm((current) => ({ ...current, hours: normalizeOperatingHours(hours) }));
  const planIndex = stepOrder.indexOf("plan");
  const showChargesSummary = index >= planIndex && step !== "publish";
  const chargesAddons = normalizePlanAddons(form.plan_addons);
  const chargesOneTime = normalizePlanOneTime(form.plan_one_time);

  const content = {
    "business-type": <BusinessType data={data} selectedType={form.type_id} setSelectedType={(value) => setForm({ ...form, type_id: value })} />,
    "business-details": <BusinessDetails form={form} setForm={setForm} generateDescription={generateDescription} correctSpelling={correctSpelling} generatingCopy={generatingCopy} correctingCopy={correctingCopy} />,
    plan: <OnboardingPlanStep form={form} setForm={setForm} />,
    brand: <BrandStep form={form} setForm={setForm} isAuthenticated={isAuthenticated} />,
    theme: <ThemeStep themes={data?.themes || []} form={form} setForm={setForm} />,
    location: <LocationStep form={form} setForm={setForm} />,
    hours: <HoursStep hours={form.hours} setHours={setHours} />,
    products: <ProductsStep form={form} setForm={setForm} />,
    delivery: <DeliveryStep form={form} setForm={setForm} />,
    payments: <PaymentsStep form={form} setForm={setForm} billingConfig={billingConfig} refresh={refresh} />,
    domain: <DomainStep form={form} setForm={setForm} business={business} refresh={refresh} />,
    legal: <LegalStep form={form} setForm={setForm} />,
    preview: <PreviewStep form={form} selectedConfig={selectedConfig} previewProducts={previewProducts} previewPhotos={previewPhotos} previewMode={previewMode} setPreviewMode={setPreviewMode} setForm={setForm} setHours={setHours} saveStep={saveStep} />,
    agreements: <AgreementsStep form={form} setForm={setForm} onContinue={next} />,
    subscription: (
      <SubscriptionStep
        form={form}
        setForm={setForm}
        business={business}
        currency={form.currency}
        billingConfig={billingConfig}
        legalGaps={legalGaps}
      />
    ),
    publish: <PublishStep form={form} />,
  };

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <main className="page-shell grid min-h-screen place-items-center p-6" data-testid="onboarding-auth-required"><section className="max-w-lg rounded-lg border bg-white p-8 text-center"><h1 className="text-3xl font-extrabold" data-testid="onboarding-auth-required-title">Create an account first</h1><p className="mt-3 text-muted-foreground" data-testid="onboarding-auth-required-body">Milestone 4 creates a real tenant storefront from onboarding answers.</p><Link data-testid="onboarding-auth-required-signup-link" to="/signup"><Button className="tap-target mt-5 rounded-lg">Go to signup</Button></Link></section></main>;
  return (
    <OnboardingI18nContext.Provider value={i18nValue}>
    <main className="page-shell min-h-screen px-3 py-3 sm:px-4 sm:py-5" data-testid="onboarding-page">
      <section className="mx-auto flex min-h-[calc(100vh-24px)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border bg-white/88 shadow-xl sm:min-h-[calc(100vh-40px)] sm:rounded-[28px]" data-testid="onboarding-shell">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b p-4 sm:gap-4 sm:p-5" data-testid="onboarding-header">
          <LogoMark />
          <div className="min-w-0 flex-1 basis-[min(100%,16rem)] sm:min-w-[220px] md:max-w-md" data-testid="onboarding-progress-wrap">
            <div className="mb-2 flex items-center justify-between text-sm font-bold">
              <span data-testid="onboarding-step-label">{t("chrome.stepOf", { current: index + 1, total: totalSteps })}</span>
              <span data-testid="onboarding-progress-value">{progress}%</span>
            </div>
            <Progress data-testid="onboarding-progress-bar" value={progress} />
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <OnboardingLanguageToggle />
            <button
              type="button"
              data-testid="onboarding-skip-dashboard-link"
              className={`text-sm font-bold ${subscriptionPaid ? "text-muted-foreground" : "cursor-not-allowed text-muted-foreground/50"}`}
              onClick={skipToDashboard}
              aria-disabled={!subscriptionPaid}
            >
              <span className="sm:hidden">{t("chrome.dashboardShort")}</span>
              <span className="hidden sm:inline">{t("chrome.skipToDashboard")}</span>
            </button>
          </div>
        </header>

        <div className="border-b px-4 py-3 md:hidden" data-testid="onboarding-mobile-step-strip">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("chrome.now")}</p>
          <p className="mt-1 text-sm font-extrabold text-[#173B2F]">{t(stepLabelKey(step))}</p>
          <div className="-mx-1 mt-3 flex gap-2 overflow-x-auto px-1 pb-1" data-testid="onboarding-mobile-step-chips">
            {stepOrder.map((stepId, stepIndex) => {
              const completed = stepIndex < index;
              const current = stepIndex === index;
              return (
                <button
                  key={stepId}
                  type="button"
                  data-testid={`onboarding-mobile-chip-${stepId}`}
                  disabled={!completed && !current}
                  onClick={() => completed && navigate(`/onboarding/${stepId}`)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold ${
                    current
                      ? "bg-[#173B2F] text-white"
                      : completed
                        ? "bg-[#EAF2E8] text-[#173B2F]"
                        : "bg-[#F3F0EA] text-muted-foreground"
                  }`}
                >
                  {stepIndex + 1}. {t(stepLabelKey(stepId))}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid flex-1 gap-5 p-4 md:grid-cols-[.8fr_1.2fr] md:gap-8 md:p-8" data-testid="onboarding-content-grid">
          <aside className="hidden flex-col rounded-2xl bg-[#173B2F] p-7 text-white md:flex" data-testid="onboarding-context-panel">
            {index === 0 ? (
              <p className="text-sm font-bold text-white/70" data-testid="onboarding-context-kicker">{t("chrome.underAnHour")}</p>
            ) : null}
            <h1 className={`${index === 0 ? "mt-4" : ""} text-3xl font-extrabold leading-tight sm:text-4xl`} data-testid="onboarding-title">{t("chrome.sidebarTitle", { businessType: selectedConfig?.display_name || "Restaurant" })}</h1>
            <p className="mt-4 leading-7 text-white/74" data-testid="onboarding-body">{t("chrome.sidebarBody", { count: totalSteps })}</p>
            {index === 0 ? (
              <p className="mt-3 text-[11px] leading-5 text-white/55" data-testid="onboarding-timing-disclaimer">
                {t("chrome.timingDisclaimer")}
              </p>
            ) : null}
            <div className="mt-6 rounded-lg bg-white/10 p-4 text-sm" data-testid="onboarding-selected-config">{selectedConfig?.display_name || "Restaurant"} · {selectedConfig?.terminology?.catalog || "menu"}</div>
            {showChargesSummary ? (
              <PlanChargesSummary
                className="mt-4"
                compact
                variant="dark"
                addons={chargesAddons}
                oneTime={chargesOneTime}
                testId="onboarding-sidebar-charges"
              />
            ) : null}
            <OnboardingStepChecklist
              currentIndex={index}
              onSelectStep={(stepId) => navigate(`/onboarding/${stepId}`)}
              legalWarning={legalChecklistWarning}
            />
          </aside>
          <section className="flex min-w-0 flex-col" data-testid="onboarding-active-step">
            <div className="flex-1">{content[step] || content["business-type"]}</div>
            {showChargesSummary && step !== "plan" && step !== "subscription" ? (
              <PlanChargesSummary
                className="mt-6"
                compact
                addons={chargesAddons}
                oneTime={chargesOneTime}
                testId="onboarding-footer-charges"
              />
            ) : null}
            <div className="mt-8 flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between" data-testid="onboarding-actions">
              <Button data-testid="onboarding-back-button" type="button" variant="outline" className="tap-target w-full rounded-lg sm:w-auto" onClick={back}>
                <ArrowLeft className="h-4 w-4" /> {t("actions.back")}
              </Button>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
                {!["business-type", "business-details", "plan", "agreements", "subscription", "publish"].includes(step) && (
                  <Button data-testid="onboarding-skip-step-button" variant="ghost" className="tap-target w-full rounded-lg sm:w-auto" onClick={skipStep}>{t("actions.skipForNow")}</Button>
                )}
                {step === "agreements" ? null : (
                <Button data-testid="onboarding-next-button" className="tap-target w-full rounded-lg font-bold sm:w-auto" onClick={next} disabled={nextDisabled}>
                  {step === "publish"
                    ? t("actions.goToDashboard")
                    : step === "subscription" && !form.subscription_active
                      ? (payingSubscription
                        ? t("actions.processing")
                        : (
                          <>
                            <span className="sm:hidden">{t("actions.payAndGoLive")}</span>
                            <span className="hidden sm:inline">{t("actions.payWithStripeAndGoLive")}</span>
                          </>
                        ))
                      : t("actions.continue")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
                )}
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
    </OnboardingI18nContext.Provider>
  );
}

function OnboardingLanguageToggle() {
  const { lang, setLang, t } = useOnboardingI18n();
  return (
    <label className="shrink-0" data-testid="onboarding-ui-language-toggle">
      <span className="sr-only">{t("chrome.langLabel")}</span>
      <select
        data-testid="onboarding-ui-language-select"
        className="h-9 rounded-lg border bg-white px-2 text-xs font-extrabold text-[#173B2F]"
        value={lang}
        onChange={(event) => setLang(event.target.value)}
        aria-label={t("chrome.langLabel")}
      >
        {ONBOARDING_UI_LANGUAGES.map((option) => (
          <option key={option.code} value={option.code}>
            {option.label} · {option.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function OnboardingStepChecklist({ currentIndex, onSelectStep, legalWarning = false }) {
  const { t } = useOnboardingI18n();
  return (
    <nav className="mt-6 flex-1" data-testid="onboarding-step-checklist" aria-label="Onboarding steps">
      <p className="mb-3 text-xs font-bold uppercase tracking-wide text-white/55">{t("chrome.yourProgress")}</p>
      <ol className="space-y-1.5">
        {stepOrder.map((stepId, stepIndex) => {
          const completed = stepIndex < currentIndex;
          const current = stepIndex === currentIndex;
          const warning = stepId === "legal" && legalWarning;
          const label = t(stepLabelKey(stepId));
          const status = warning ? "warning" : completed ? "completed" : current ? "current" : "upcoming";
          return (
            <li key={stepId}>
              <button
                type="button"
                data-testid={`onboarding-checklist-${stepId}`}
                data-status={status}
                disabled={!completed && !current && !warning}
                onClick={() => (completed || warning) && onSelectStep?.(stepId)}
                className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                  current
                    ? "bg-white/16 font-extrabold text-white"
                    : warning
                      ? "font-bold text-amber-100 hover:bg-white/10"
                      : completed
                        ? "font-bold text-white/90 hover:bg-white/10"
                        : "cursor-default font-medium text-white/45"
                }`}
              >
                <span
                  className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border text-[10px] ${
                    warning
                      ? "border-amber-400 bg-amber-400 text-[#173B2F]"
                      : completed
                        ? "border-[#7CBA6F] bg-[#7CBA6F] text-[#173B2F]"
                        : current
                          ? "border-white bg-white text-[#173B2F]"
                          : "border-white/30 bg-transparent text-transparent"
                  }`}
                  aria-hidden="true"
                >
                  {warning ? (
                    <AlertTriangle className="h-3 w-3" strokeWidth={3} />
                  ) : completed ? (
                    <Check className="h-3 w-3" strokeWidth={3} />
                  ) : current ? (
                    stepIndex + 1
                  ) : (
                    ""
                  )}
                </span>
                <span className="min-w-0 truncate">{label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function StepIntro({ title, body }) { return <><h2 className="text-2xl font-extrabold sm:text-3xl" data-testid="onboarding-step-title">{title}</h2><p className="mt-2 text-sm leading-6 text-muted-foreground" data-testid="onboarding-step-body">{body}</p></>; }
function BusinessType({ data, selectedType, setSelectedType }) {
  const { t } = useOnboardingI18n();
  const types = [...(data?.business_types || [])].sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));
  return (
    <div>
      <StepIntro title={t("intro.businessType.title")} body={t("intro.businessType.body")} />
      <div className="mt-6 grid gap-3">
        {types.map((type) => {
          const Icon = BUSINESS_TYPE_ICONS[type.type_id] || Store;
          return (
            <button
              key={type.type_id}
              type="button"
              data-testid={`business-type-${type.type_id}-card`}
              onClick={() => setSelectedType(type.type_id)}
              className={`tap-target flex gap-3 rounded-lg border p-5 text-left transition-[transform,border-color,background-color] hover:-translate-y-0.5 ${selectedType === type.type_id ? "border-primary bg-primary/8" : "bg-white"}`}
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" data-testid={`business-type-${type.type_id}-icon`} />
              <div>
                <h3 className="font-bold" data-testid={`business-type-${type.type_id}-title`}>{type.display_name}</h3>
                <p className="mt-1 text-sm text-muted-foreground" data-testid={`business-type-${type.type_id}-description`}>{type.description}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
function BusinessDetails({ form, setForm, generateDescription, correctSpelling, generatingCopy, correctingCopy }) {
  const { t } = useOnboardingI18n();
  const descriptionLen = String(form.description || "").length;
  return (
    <div>
      <StepIntro title={t("intro.businessDetails.title")} body={t("intro.businessDetails.body")} />
      <div className="mt-6 space-y-5">
        <ControlledField label="Business name" testId="business-name-input" value={form.name} onChange={(name) => setForm({ ...form, name, slug: slugify(name), hero_headline: `Welcome to ${name}` })} />
        <div>
          <div className="mb-2 flex items-end justify-between gap-2">
            <Label htmlFor="business-description-textarea" data-testid="business-description-label">Short description</Label>
            <span className="text-xs tabular-nums text-muted-foreground" data-testid="business-description-count">
              {descriptionLen}/220
            </span>
          </div>
          <Textarea
            id="business-description-textarea"
            data-testid="business-description-textarea"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 220) })}
            maxLength={220}
            className="min-h-28"
          />
        </div>
        <ControlledField label="Hero headline" testId="hero-headline-input" value={form.hero_headline} onChange={(hero_headline) => setForm({ ...form, hero_headline })} />
        <ControlledField
          label="Hero subtext"
          testId="hero-subtext-input"
          value={form.hero_subtext}
          onChange={(hero_subtext) => setForm({ ...form, hero_subtext: String(hero_subtext).slice(0, 120) })}
          maxLength={120}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="generate-description-button"
            variant="outline"
            className="tap-target rounded-lg"
            onClick={generateDescription}
            disabled={generatingCopy || correctingCopy}
          >
            {generatingCopy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {generatingCopy ? "Generating…" : "Generate with AI"}
          </Button>
          <Button
            data-testid="spell-correct-button"
            variant="outline"
            className="tap-target rounded-lg"
            onClick={correctSpelling}
            disabled={generatingCopy || correctingCopy}
          >
            {correctingCopy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SpellCheck className="h-4 w-4" />}
            {correctingCopy ? "Correcting…" : "Fix spelling with AI"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="business-details-ai-hint">
          Generate polishes the text you typed for the description and hero subtext. Spell check only corrects typos in the description, headline, and subtext.
        </p>
      </div>
    </div>
  );
}
function resolveUploadUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}${path}`;
}

/** Persist relative API paths so logos work across environments (strip expiring signatures). */
function toStoredMediaUrl(path) {
  if (!path) return "";
  const value = String(path).trim();
  if (!value) return "";
  const backend = String(BACKEND_URL || "").replace(/\/$/, "");
  if (backend && value.startsWith(backend)) {
    const stripped = value.slice(backend.length) || "/";
    return stripMediaSignature(stripped);
  }
  try {
    if (/^https?:\/\//i.test(value)) {
      const parsed = new URL(value);
      if (parsed.pathname.startsWith("/api/")) {
        return stripMediaSignature(`${parsed.pathname}${parsed.search}`);
      }
    }
  } catch {
    /* keep original */
  }
  return stripMediaSignature(value);
}

function stripMediaSignature(path) {
  const value = String(path || "");
  const match = value.match(/(\/api\/v1\/files\/[^/?#]+\/download)/i);
  if (!match) return value.split("?")[0] || value;
  try {
    const parsed = value.startsWith("http") ? new URL(value) : new URL(value, "http://local.invalid");
    const bid = parsed.searchParams.get("bid");
    return bid ? `${match[1]}?bid=${encodeURIComponent(bid)}` : match[1];
  } catch {
    return match[1];
  }
}

function uploadErrorMessage(error) {
  const detail = error.response?.data?.detail;
  return detail?.error?.message || error.message || "Upload failed. Please try again.";
}

async function persistBrandMedia({ logoUrl, photos }) {
  await apiPost("/onboarding/save", {
    step: "brand",
    data: {
      logo_url: toStoredMediaUrl(logoUrl || ""),
      photos: (photos || []).map(toStoredMediaUrl).filter(Boolean),
    },
  });
}

function BrandStep({ form, setForm, isAuthenticated }) {
  const { t } = useOnboardingI18n();
  const [logoUploading, setLogoUploading] = useState(false);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const photoUrls = form.photos_text.split("\n").map((item) => item.trim()).filter(Boolean);

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setLogoUploading(true);
    setUploadError("");
    try {
      const uploaded = await uploadFile(file);
      const nextLogo = uploaded.url;
      setForm({ ...form, logo_url: nextLogo });
      if (isAuthenticated) {
        await persistBrandMedia({ logoUrl: nextLogo, photos: photoUrls });
      }
      toast.success("Logo uploaded");
    } catch (error) {
      const message = uploadErrorMessage(error);
      setUploadError(message);
      toast.error(message);
    } finally {
      setLogoUploading(false);
    }
  };

  const uploadPhotos = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setPhotosUploading(true);
    setUploadError("");
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const uploaded = await uploadFile(file);
        uploadedUrls.push(uploaded.url);
      }
      const nextPhotos = [...photoUrls, ...uploadedUrls];
      setForm({ ...form, photos_text: nextPhotos.join("\n") });
      if (isAuthenticated) {
        await persistBrandMedia({ logoUrl: form.logo_url, photos: nextPhotos });
      }
      toast.success(files.length === 1 ? "Photo uploaded" : `${files.length} photos uploaded`);
    } catch (error) {
      const message = uploadErrorMessage(error);
      setUploadError(message);
      toast.error(message);
    } finally {
      setPhotosUploading(false);
    }
  };

  const removePhoto = (index) => {
    const nextPhotos = photoUrls.filter((_, photoIndex) => photoIndex !== index);
    setForm({ ...form, photos_text: nextPhotos.join("\n") });
    if (isAuthenticated) {
      persistBrandMedia({ logoUrl: form.logo_url, photos: nextPhotos }).catch(() => {});
    }
  };

  return (
    <div>
      <StepIntro title={t("intro.brand.title")} body={t("intro.brand.body")} />
      <div className="mt-6 space-y-6">
        <div className="space-y-3">
          <Label data-testid="logo-upload-label">Logo</Label>
          {form.logo_url ? (
            <div className="flex items-center gap-4 rounded-lg border bg-[#F8F5EE] p-4" data-testid="logo-preview-wrap">
              <img data-testid="logo-preview-image" src={resolveUploadUrl(form.logo_url)} alt="Uploaded logo" className="h-20 w-20 rounded-lg border bg-white object-contain" />
              <div className="flex-1">
                <p className="text-sm font-bold" data-testid="logo-preview-label">Logo ready</p>
                <p className="text-xs text-muted-foreground">Upload a new file to replace it.</p>
              </div>
            </div>
          ) : null}
          <label
            data-testid="logo-upload-box"
            className="grid min-h-28 cursor-pointer place-items-center rounded-lg border border-dashed bg-[#F8F5EE] p-5 text-center transition-colors hover:border-primary"
          >
            <input
              data-testid="logo-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
              className="sr-only"
              disabled={logoUploading || photosUploading}
              onChange={uploadLogo}
            />
            {logoUploading ? <Loader2 className="h-8 w-8 animate-spin text-primary" /> : <ImagePlus className="h-8 w-8 text-primary" />}
            <span className="mt-2 font-bold" data-testid="logo-upload-title">{logoUploading ? "Uploading logo…" : "Upload logo"}</span>
            <span className="text-xs text-muted-foreground" data-testid="logo-upload-hint">JPG, PNG, WebP, or GIF · max 8MB</span>
          </label>
        </div>

        <div className="space-y-3">
          <Label data-testid="photos-upload-label">Storefront photos</Label>
          {photoUrls.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="photos-preview-grid">
              {photoUrls.map((url, index) => (
                <div key={`${url}-${index}`} className="relative overflow-hidden rounded-lg border" data-testid={`photo-preview-${index}`}>
                  <img src={resolveUploadUrl(url)} alt={`Storefront photo ${index + 1}`} className="aspect-[4/3] w-full object-cover" />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute right-2 top-2 h-7 rounded-lg px-2 text-xs"
                    data-testid={`photo-remove-button-${index}`}
                    onClick={() => removePhoto(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
          <label
            data-testid="photos-upload-box"
            className="grid min-h-28 cursor-pointer place-items-center rounded-lg border border-dashed bg-[#F8F5EE] p-5 text-center transition-colors hover:border-primary"
          >
            <input
              data-testid="photos-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
              multiple
              className="sr-only"
              disabled={logoUploading || photosUploading}
              onChange={uploadPhotos}
            />
            {photosUploading ? <Loader2 className="h-8 w-8 animate-spin text-primary" /> : <ImagePlus className="h-8 w-8 text-primary" />}
            <span className="mt-2 font-bold" data-testid="photos-upload-title">{photosUploading ? "Uploading photos…" : "Upload photos"}</span>
            <span className="text-xs text-muted-foreground" data-testid="photos-upload-hint">JPG, PNG, WebP, or GIF · select one or more · max 8MB each</span>
          </label>
        </div>

        {uploadError ? (
          <p className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm font-bold text-orange-800" data-testid="brand-upload-error">
            {uploadError}
          </p>
        ) : null}

        <div className="space-y-4 border-t pt-4">
          <p className="text-sm font-bold text-muted-foreground" data-testid="brand-url-fallback-label">Or paste image URLs</p>
          <p className="text-xs text-muted-foreground" data-testid="brand-url-fallback-hint">
            Works with public https image links (JPG, PNG, WebP, GIF). Prefer upload above when you have files on your device.
          </p>
          <ControlledField label="Logo URL" testId="logo-url-input" value={form.logo_url} onChange={(logo_url) => setForm({ ...form, logo_url })} />
          <Label data-testid="photos-url-list-label">Photo URLs, one per line</Label>
          <Textarea data-testid="photos-url-list-textarea" className="min-h-28" value={form.photos_text} onChange={(event) => setForm({ ...form, photos_text: event.target.value })} />
        </div>
      </div>
    </div>
  );
}
function ThemeMiniPreview({ businessName, colors, currency = "EUR", testId }) {
  const primary = colors?.primary || "#173B2F";
  const accent = colors?.accent || "#EA6A3B";
  const background = colors?.background || "#FFFDF8";
  const text = colors?.text || "#171717";
  return (
    <div
      data-testid={testId}
      className="overflow-hidden rounded-lg border"
      style={{ background, color: text }}
    >
      <div className="px-3 py-2.5" style={{ background: primary, color: "#fff" }}>
        <p className="truncate text-sm font-extrabold leading-tight">{businessName || "Your restaurant"}</p>
        <p className="mt-0.5 truncate text-[11px] font-semibold" style={{ color: accent }}>
          Order for pickup or delivery
        </p>
      </div>
      <div className="flex items-center justify-between gap-2 px-3 py-2.5">
        <p className="truncate text-sm font-bold">Product Name</p>
        <span
          className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-extrabold text-white"
          style={{ background: accent }}
        >
          {formatMoney(9.5, currency)}
        </span>
      </div>
    </div>
  );
}

function ThemeColorField({ label, testId, value, onChange }) {
  return (
    <div>
      <Label htmlFor={testId} className="mb-2 block text-xs font-bold text-muted-foreground" data-testid={`${testId}-label`}>
        {label}
      </Label>
      <div className="flex items-center gap-2 rounded-lg border bg-white px-2 py-1.5">
        <input
          type="color"
          aria-label={`${label} color picker`}
          value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#000000"}
          onChange={(event) => onChange(event.target.value)}
          className="h-8 w-8 cursor-pointer rounded border-0 bg-transparent p-0"
          data-testid={`${testId}-swatch`}
        />
        <Input
          id={testId}
          data-testid={testId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-9 border-0 px-1 shadow-none focus-visible:ring-0"
        />
      </div>
    </div>
  );
}

function ThemeStep({ themes, form, setForm }) {
  const { t } = useOnboardingI18n();
  const oneTime = normalizePlanOneTime(form.plan_one_time);
  const customColorsSelected = oneTime.includes("custom_colors");
  const previewName = form.name || "Your restaurant";
  const customPrice = getPlanOneTime("custom_colors");

  const selectPreset = (theme) => {
    setForm({
      ...form,
      theme_id: theme.id,
      primary_color: theme.colors.primary,
      accent_color: theme.colors.accent,
      background_color: theme.colors.background || form.background_color,
      text_color: theme.colors.text || form.text_color,
      heading_font: theme.typography?.heading_font || form.heading_font,
      body_font: theme.typography?.body_font || form.body_font,
      plan_one_time: oneTime.filter((id) => id !== "custom_colors"),
    });
  };

  const enableCustomColors = () => {
    setForm({
      ...form,
      plan_one_time: oneTime.includes("custom_colors") ? oneTime : [...oneTime, "custom_colors"],
    });
  };

  const showPresetsInstead = () => {
    setForm({
      ...form,
      plan_one_time: oneTime.filter((id) => id !== "custom_colors"),
    });
  };

  if (customColorsSelected) {
    return (
      <div data-testid="theme-custom-panel">
        <StepIntro title={t("intro.theme.title")} body={t("intro.theme.customBody")} />
        <button
          type="button"
          className="mt-2 text-sm font-bold text-[#EA6A3B] hover:underline"
          data-testid="theme-use-preset-link"
          onClick={showPresetsInstead}
        >
          Use a preset theme instead
        </button>

        <div className="mt-5 rounded-2xl border-2 border-[#EA6A3B] bg-white p-5" data-testid="theme-custom-editor">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <label className="flex cursor-pointer items-start gap-3" data-testid="theme-custom-colors-toggle">
              <Checkbox checked onCheckedChange={(checked) => { if (!checked) showPresetsInstead(); }} className="mt-0.5" />
              <span className="font-extrabold text-[#173B2F]">Custom brand colors</span>
            </label>
            {customPrice ? (
              <p className="shrink-0 font-extrabold text-[#173B2F]" data-testid="theme-custom-colors-price">
                {formatMoney(customPrice.price, FOUNDATION_PLAN.currency)}
                <span className="text-xs font-bold text-muted-foreground"> {customPrice.label}</span>
              </p>
            ) : null}
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <ThemeColorField label="Primary" testId="primary-color-input" value={form.primary_color} onChange={(primary_color) => setForm({ ...form, primary_color })} />
            <ThemeColorField label="Accent" testId="accent-color-input" value={form.accent_color} onChange={(accent_color) => setForm({ ...form, accent_color })} />
            <ThemeColorField label="Background" testId="background-color-input" value={form.background_color} onChange={(background_color) => setForm({ ...form, background_color })} />
            <ThemeColorField label="Text" testId="text-color-input" value={form.text_color} onChange={(text_color) => setForm({ ...form, text_color })} />
            <ControlledField label="Heading font" testId="heading-font-input" value={form.heading_font} onChange={(heading_font) => setForm({ ...form, heading_font })} />
            <ControlledField label="Body font" testId="body-font-input" value={form.body_font} onChange={(body_font) => setForm({ ...form, body_font })} />
          </div>

          <div className="mt-6" data-testid="theme-live-preview">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Live preview</p>
            <div
              className="overflow-hidden rounded-xl border"
              style={{
                background: form.background_color,
                color: form.text_color,
                fontFamily: form.body_font || "DM Sans, sans-serif",
              }}
            >
              <div className="px-4 py-4" style={{ background: form.primary_color, color: "#fff" }}>
                <p className="text-xl font-extrabold" style={{ fontFamily: form.heading_font || "Sora, sans-serif" }}>
                  {previewName}
                </p>
                <p className="mt-1 text-sm font-semibold" style={{ color: form.accent_color }}>
                  {form.hero_subtext || "Order online for pickup or delivery"}
                </p>
              </div>
              <div className="flex items-start justify-between gap-3 px-4 py-4">
                <div className="min-w-0">
                  <p className="font-extrabold" style={{ fontFamily: form.heading_font || "Sora, sans-serif" }}>
                    Product Name
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">Product description</p>
                </div>
                <span
                  className="shrink-0 rounded-full px-3 py-1.5 text-sm font-extrabold text-white"
                  style={{ background: form.accent_color }}
                >
                  {formatMoney(9.5, form.currency || FOUNDATION_PLAN.currency)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="theme-preset-panel">
      <StepIntro
        title={t("intro.theme.title")}
        body={t("intro.theme.presetBody", { name: previewName })}
      />
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {themes.map((theme) => (
          <button
            key={theme.id}
            type="button"
            data-testid={`theme-${theme.id}-button`}
            onClick={() => selectPreset(theme)}
            className={`tap-target rounded-xl border bg-white p-3 text-left transition-colors hover:border-primary ${
              form.theme_id === theme.id ? "border-[#EA6A3B] ring-1 ring-[#EA6A3B]/30" : ""
            }`}
          >
            <ThemeMiniPreview
              businessName={previewName}
              colors={theme.colors}
              currency={form.currency || FOUNDATION_PLAN.currency}
              testId={`theme-${theme.id}-preview`}
            />
            <div className="mt-3 flex gap-2">
              {Object.values(theme.colors || {}).slice(0, 3).map((color) => (
                <span
                  key={`${theme.id}-${color}`}
                  data-testid={`theme-${theme.id}-swatch-${color}`}
                  className="h-5 w-5 rounded-full border"
                  style={{ background: color }}
                />
              ))}
            </div>
            <h3 className="mt-3 font-extrabold" data-testid={`theme-${theme.id}-name`}>{theme.name}</h3>
            <p className="mt-0.5 text-sm text-muted-foreground" data-testid={`theme-${theme.id}-description`}>{theme.description}</p>
          </button>
        ))}
      </div>

      <section className="mt-6" data-testid="theme-addon-section">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Optional add-on</p>
        <button
          type="button"
          data-testid="theme-addon-custom-colors"
          onClick={enableCustomColors}
          className="flex w-full items-start justify-between gap-3 rounded-xl border bg-white p-4 text-left hover:border-[#EA6A3B]"
        >
          <div>
            <p className="font-extrabold text-[#173B2F]">Custom brand colors</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Go beyond the 6 preset themes</p>
          </div>
          {customPrice ? (
            <p className="shrink-0 font-extrabold text-[#173B2F]">
              {formatMoney(customPrice.price, FOUNDATION_PLAN.currency)}
              <span className="text-xs font-bold text-muted-foreground"> {customPrice.label}</span>
            </p>
          ) : null}
        </button>
      </section>
    </div>
  );
}
function buildMapQuery(form) {
  return [form.address_line1, form.address_line2, form.city, form.postcode, form.country]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
}

function MapPreview({ query, visible }) {
  if (!visible) {
    return (
      <div className="grid h-48 place-items-center rounded-lg border bg-[#EAF2E8] text-center" data-testid="map-preview-hidden">
        <MapPin className="h-8 w-8 text-accent" />
        <span className="mt-2 text-sm font-bold">Address hidden on storefront</span>
        <span className="text-xs text-muted-foreground">Turn on “Show address on storefront” to preview the map.</span>
      </div>
    );
  }

  if (!query || query.length < 5) {
    return (
      <div className="grid h-48 place-items-center rounded-lg border bg-[#EAF2E8] text-center" data-testid="map-preview-placeholder">
        <MapPin className="h-8 w-8 text-accent" />
        <span className="mt-2 text-sm font-bold">Enter an address to preview the map</span>
      </div>
    );
  }

  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
  const openHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  return (
    <div className="overflow-hidden rounded-lg border" data-testid="map-preview">
      <iframe
        title="Map preview"
        data-testid="map-preview-iframe"
        src={mapSrc}
        className="h-48 w-full border-0"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      <a
        href={openHref}
        target="_blank"
        rel="noopener noreferrer"
        className="block border-t bg-[#F8F5EE] px-4 py-2 text-center text-sm font-bold text-primary"
        data-testid="map-preview-open-link"
      >
        Open in Google Maps
      </a>
    </div>
  );
}

function LocationStep({ form, setForm }) {
  const { t } = useOnboardingI18n();
  const mapQuery = buildMapQuery(form);
  const enabledLanguages = normalizeEnabledLanguages(form.enabled_languages, form.default_language);
  const toggleLanguage = (code) => {
    const current = normalizeEnabledLanguages(form.enabled_languages, form.default_language);
    if (code === form.default_language) return;
    const next = current.includes(code)
      ? current.filter((entry) => entry !== code)
      : [...current, code];
    setForm({ ...form, enabled_languages: normalizeEnabledLanguages(next, form.default_language) });
  };

  return (
    <div>
      <StepIntro title={t("intro.location.title")} body={t("intro.location.body")} />
      <div className="mt-6 space-y-4">
        <ControlledField label="Address line 1" testId="address-line1-input" value={form.address_line1} onChange={(address_line1) => setForm({ ...form, address_line1 })} />
        <ControlledField label="Address line 2" testId="address-line2-input" value={form.address_line2} onChange={(address_line2) => setForm({ ...form, address_line2 })} />
        <ControlledField label="City" testId="city-input" value={form.city} onChange={(city) => setForm({ ...form, city })} />
        <ControlledField label="Postcode" testId="postcode-input" value={form.postcode} onChange={(postcode) => setForm({ ...form, postcode })} />
        <ControlledField
          label="Country"
          testId="country-input"
          value={form.country}
          onChange={(country) => setForm({
            ...form,
            country,
            currency: suggestCurrencyForCountry(country),
              default_language: suggestLanguageForCountry(country),
              enabled_languages: normalizeEnabledLanguages(
                form.enabled_languages,
                suggestLanguageForCountry(country),
              ),
          })}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <ControlledField label="Public phone" testId="onboarding-phone-input" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
          <ControlledField label="Public email" testId="onboarding-email-input" value={form.email} onChange={(email) => setForm({ ...form, email })} />
        </div>
        <p className="text-xs text-muted-foreground" data-testid="onboarding-contact-hint">
          Customers see these in the footer and location section. This is separate from your login email.
        </p>
        <div>
          <label htmlFor="onboarding-currency-select" className="text-sm font-bold" data-testid="onboarding-currency-label">Store currency</label>
          <select
            id="onboarding-currency-select"
            data-testid="onboarding-currency-select"
            className="mt-2 h-12 w-full rounded-lg border bg-white px-3 font-bold"
            value={form.currency || "GBP"}
            onChange={(event) => setForm({ ...form, currency: event.target.value })}
          >
            {CURRENCY_OPTIONS.map((option) => (
              <option key={option.code} value={option.code}>{option.symbol} {option.code} · {option.name}</option>
            ))}
          </select>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="onboarding-currency-hint">Menu and checkout prices will use this symbol.</p>
        </div>
        <div>
          <label htmlFor="onboarding-catalog-language-select" className="text-sm font-bold" data-testid="onboarding-catalog-language-label">{t("location.catalogLanguage")}</label>
          <select
            id="onboarding-catalog-language-select"
            data-testid="onboarding-catalog-language-select"
            className="mt-2 h-12 w-full rounded-lg border bg-white px-3 font-bold"
            value={form.default_language || "en"}
            onChange={(event) => setForm({
              ...form,
              default_language: event.target.value,
              enabled_languages: normalizeEnabledLanguages(
                form.enabled_languages,
                event.target.value,
              ),
            })}
          >
            {STOREFRONT_LANGUAGES.map((option) => (
              <option key={option.code} value={option.code}>{option.name}</option>
            ))}
          </select>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="onboarding-catalog-language-hint">
            {t("location.catalogHint")}
          </p>
        </div>
        <div className="rounded-xl border bg-[#F8F5EE] p-4" data-testid="onboarding-language-toggle-group">
          <p className="text-sm font-bold">{t("location.storefrontToggle")}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("location.storefrontHint")}
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {STOREFRONT_LANGUAGES.map((option) => {
              const locked = option.code === form.default_language;
              return (
                <label
                  key={option.code}
                  className={`flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm ${locked ? "opacity-85" : ""}`}
                  data-testid={`onboarding-language-toggle-${option.code}`}
                >
                  <Checkbox
                    checked={enabledLanguages.includes(option.code)}
                    disabled={locked}
                    onCheckedChange={() => toggleLanguage(option.code)}
                    data-testid={`onboarding-language-toggle-checkbox-${option.code}`}
                  />
                  <span className="font-semibold">{option.name}</span>
                  {locked ? <span className="ml-auto text-xs text-muted-foreground">{t("location.default")}</span> : null}
                </label>
              );
            })}
          </div>
        </div>
        <ToggleRow label="Show address on storefront" testId="show-address-toggle" checked={form.show_address} onChange={(show_address) => setForm({ ...form, show_address })} />
        <MapPreview query={mapQuery} visible={form.show_address} />
      </div>
    </div>
  );
}
function HoursStep({ hours, setHours }) {
  const { t } = useOnboardingI18n();

  return (
    <div>
      <StepIntro
        title={t("intro.hours.title")}
        body={t("intro.hours.body")}
      />
      <div className="mt-6">
        <HoursEditor hours={hours} onChange={setHours} testIdPrefix="hours" />
      </div>
    </div>
  );
}
function ProductsStep({ form, setForm }) {
  const { t } = useOnboardingI18n();
  const { format } = useCurrency(form.currency);
  const currencySymbol = getCurrencySymbol(form.currency);
  const displayProducts = form.parsed_products.length ? form.parsed_products : parseMenuLines(form.menu_text).slice(0, 6);
  const oneTime = normalizePlanOneTime(form.plan_one_time);
  const aiCreditsSelected = oneTime.includes("ai_image_credits");

  const handleImportedProducts = (products) => {
    setForm((current) => ({
      ...current,
      parsed_products: products,
      menu_text: products.map((item) => `${item.name} ${format(item.price)}`).join("\n"),
    }));
  };

  return (
    <div>
      <StepIntro
        title={t("intro.products.title")}
        body={t("intro.products.body")}
      />
      <MenuImportPanel testIdPrefix="onboarding-menu-import" onImported={handleImportedProducts} />
      <div className="mt-6 rounded-xl border bg-white p-5">
        <p className="font-extrabold" data-testid="onboarding-menu-paste-title">Or paste menu text</p>
        <p className="mt-1 text-sm text-muted-foreground">One dish per line with price, e.g. Burrata {currencySymbol}9.50</p>
        <Textarea
          data-testid="menu-paste-textarea"
          className="mt-4 min-h-32"
          value={form.menu_text}
          onChange={(event) => setForm({ ...form, menu_text: event.target.value, parsed_products: [] })}
        />
      </div>
      <div className="mt-5 grid gap-2" data-testid="parsed-products-list">
        {displayProducts.map((item, index) => (
          <div key={`${item.name}-${index}`} className="rounded-lg border p-3" data-testid={`parsed-product-${index}`}>
            <strong>{item.name}</strong>
            <span className="ml-2 text-sm text-muted-foreground">{format(item.price)} · {item.category}</span>
          </div>
        ))}
        {!displayProducts.length && (
          <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground" data-testid="parsed-products-empty">
            Upload a menu file or paste text to preview products here.
          </p>
        )}
      </div>
      <section className="mt-6" data-testid="products-addon-section">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Optional add-on for product photos</p>
        <PlanAddonOffer
          kind="one_time"
          id="ai_image_credits"
          selected={aiCreditsSelected}
          onToggle={() => setForm({ ...form, plan_one_time: togglePlanSelection(oneTime, "ai_image_credits") })}
          testId="products-addon-ai-credits"
        />
        <p className="mt-2 text-xs text-muted-foreground" data-testid="products-addon-ai-credits-hint">
          Added to your checkout before you go live. You can also top up from Settings anytime after launch.
        </p>
      </section>
    </div>
  );
}
function DeliveryStep({ form, setForm }) {
  const { t } = useOnboardingI18n();
  const { format } = useCurrency(form.currency);
  const platformLinks = normalizeDeliveryPlatformLinks(form.delivery_platform_links);
  const usedNames = new Set(platformLinks.map((item) => item.name.toLowerCase()));
  const radiusUnit = form.delivery_radius_unit === "km" ? "km" : "mi";
  const addons = normalizePlanAddons(form.plan_addons);
  const cateringSelected = addons.includes("catering");
  const leadHours = normalizeCateringLeadHours(form.catering_min_lead_hours, 48);
  const noticeHours = normalizeReservationNoticeHours(form.reservation_min_notice_hours, 2);
  const partyMin = normalizePartySize(form.reservation_party_min, 2);
  const partyMax = Math.max(partyMin, normalizePartySize(form.reservation_party_max, 8));

  const setLinks = (delivery_platform_links) => setForm({ ...form, delivery_platform_links });

  const addPlatform = (name = "") => {
    setLinks([...platformLinks, createDeliveryPlatformLink({ name })]);
  };

  const updatePlatform = (id, patch) => {
    setLinks(platformLinks.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const removePlatform = (id) => {
    setLinks(platformLinks.filter((item) => item.id !== id));
  };

  const changeRadiusUnit = (nextUnit) => {
    if (nextUnit === radiusUnit) return;
    const current = Number(form.delivery_radius);
    let nextRadius = form.delivery_radius;
    if (Number.isFinite(current) && current > 0) {
      const converted = nextUnit === "km" ? current * 1.60934 : current / 1.60934;
      nextRadius = String(Math.round(converted * 10) / 10);
    }
    setForm({ ...form, delivery_radius_unit: nextUnit, delivery_radius: nextRadius });
  };

  return (
    <div>
      <StepIntro
        title={t("intro.delivery.title")}
        body={t("intro.delivery.body")}
      />
      <div className="mt-6 space-y-4">
        <ToggleRow label="Pickup" testId="pickup-toggle" checked={form.pickup_enabled} onChange={(pickup_enabled) => setForm({ ...form, pickup_enabled })} />
        <ToggleRow label="Delivery" testId="delivery-toggle" checked={form.delivery_enabled} onChange={(delivery_enabled) => setForm({ ...form, delivery_enabled })}>
          <div className="grid gap-4" data-testid="delivery-settings-fields">
            <div data-testid="delivery-radius-field">
              <Label htmlFor="delivery-radius-input" data-testid="delivery-radius-input-label">Delivery radius</Label>
              <div className="mt-2 flex gap-2">
                <Input
                  id="delivery-radius-input"
                  data-testid="delivery-radius-input"
                  type="number"
                  min="0"
                  step="0.1"
                  className="h-12 flex-1 rounded-lg bg-[#F8F5EE]"
                  value={form.delivery_radius}
                  onChange={(event) => setForm({ ...form, delivery_radius: event.target.value })}
                />
                <select
                  id="delivery-radius-unit-select"
                  data-testid="delivery-radius-unit-select"
                  className="h-12 w-[7.5rem] shrink-0 rounded-lg border bg-white px-3 font-bold"
                  value={radiusUnit}
                  onChange={(event) => changeRadiusUnit(event.target.value)}
                  aria-label="Delivery radius unit"
                >
                  <option value="mi">Miles</option>
                  <option value="km">Kilometers</option>
                </select>
              </div>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="delivery-radius-unit-hint">
                Shown on your storefront as {radiusUnit === "km" ? "kilometers" : "miles"}. Switching units converts the distance.
              </p>
            </div>
            <PriceField label="Delivery fee" testId="delivery-fee-input" currency={form.currency} value={form.delivery_fee} onChange={(delivery_fee) => setForm({ ...form, delivery_fee })} />
            <PriceField label="Delivery minimum (menu items)" testId="min-order-value-input" currency={form.currency} value={form.min_order_value} onChange={(min_order_value) => setForm({ ...form, min_order_value })} />
            <p className="text-xs text-muted-foreground" data-testid="min-order-value-hint">Applies to delivery only. Pickup has no minimum. Delivery fee does not count toward this amount.</p>
            <ControlledField label="Estimated delivery time" testId="delivery-time-input" value={form.delivery_time} onChange={(delivery_time) => setForm({ ...form, delivery_time })} />
          </div>
        </ToggleRow>
        <ControlledField label="Pickup instructions" testId="pickup-instructions-input" value={form.pickup_instructions} onChange={(pickup_instructions) => setForm({ ...form, pickup_instructions })} />
        <ToggleRow
          label="Accept reservations"
          description="Let customers request a table, slot, or appointment. Nothing is auto-confirmed — you accept or decline each request."
          testId="reservations-toggle"
          checked={Boolean(form.reservations_enabled)}
          onChange={(reservations_enabled) => setForm({ ...form, reservations_enabled })}
        >
          <div className="grid gap-4 sm:grid-cols-2" data-testid="reservation-settings-fields">
            <div data-testid="reservation-party-size-field">
              <Label data-testid="reservation-party-size-label">Party size range</Label>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  id="reservation-party-min-input"
                  data-testid="reservation-party-min-input"
                  type="number"
                  min="1"
                  className="h-12 rounded-lg bg-[#F8F5EE]"
                  value={form.reservation_party_min}
                  onChange={(event) => setForm({ ...form, reservation_party_min: event.target.value })}
                  aria-label="Minimum party size"
                />
                <span className="text-sm text-muted-foreground">–</span>
                <Input
                  id="reservation-party-max-input"
                  data-testid="reservation-party-max-input"
                  type="number"
                  min={partyMin}
                  className="h-12 rounded-lg bg-[#F8F5EE]"
                  value={form.reservation_party_max}
                  onChange={(event) => setForm({ ...form, reservation_party_max: event.target.value })}
                  aria-label="Maximum party size"
                />
                <span className="shrink-0 text-sm text-muted-foreground">guests</span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="reservation-party-size-hint">
                Currently {partyMin} – {partyMax} guests.
              </p>
            </div>
            <div data-testid="reservation-notice-field">
              <Label htmlFor="reservation-min-notice-select" data-testid="reservation-notice-label">Minimum notice</Label>
              <select
                id="reservation-min-notice-select"
                data-testid="reservation-min-notice-select"
                className="mt-2 h-12 w-full rounded-lg border bg-[#F8F5EE] px-3 text-sm font-medium"
                value={noticeHours}
                onChange={(event) => setForm({
                  ...form,
                  reservation_min_notice_hours: normalizeReservationNoticeHours(event.target.value, 2),
                })}
              >
                {RESERVATION_NOTICE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </ToggleRow>
      </div>

      <section className="mt-6 space-y-3" data-testid="delivery-addon-section">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Optional add-ons</p>
        <PlanAddonOffer
          kind="monthly"
          id="catering"
          selected={cateringSelected}
          onToggle={() => setForm({
            ...form,
            plan_addons: togglePlanSelection(addons, "catering"),
          })}
          testId="delivery-addon-catering"
        >
          <div
            className="rounded-xl bg-[#F5F2E8] p-4"
            data-testid="delivery-addon-catering-lead-time"
          >
            <p className="font-extrabold text-[#2c2c2a]">Minimum lead time</p>
            <p className="mt-1 text-sm text-muted-foreground">
              How much notice do you need before a pre-order date?
            </p>
            <label htmlFor="catering-min-lead-hours" className="sr-only">
              Minimum lead time
            </label>
            <select
              id="catering-min-lead-hours"
              data-testid="delivery-addon-catering-lead-select"
              className="mt-3 h-11 w-full rounded-lg border border-[#E5DFD4] bg-white px-3 text-sm font-medium text-[#2c2c2a]"
              value={leadHours}
              onChange={(event) => setForm({
                ...form,
                catering_min_lead_hours: normalizeCateringLeadHours(event.target.value, 48),
              })}
            >
              {CATERING_LEAD_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-3 text-sm text-muted-foreground">
              Set order limits, blackout dates, and deposits after you go live, from your dashboard. Not for table bookings — those are free with Book & Reserve.
            </p>
          </div>
        </PlanAddonOffer>
        <PlanAddonOffer
          kind="monthly"
          id="multi_courier"
          selected={false}
          disabled
          testId="delivery-addon-multi-courier"
        />
      </section>

      <section className="mt-8 rounded-xl border bg-[#F8F5EE] p-5" data-testid="delivery-platform-links-section">
        <p className="font-extrabold text-[#173B2F]" data-testid="delivery-platform-links-title"> Link to your delivery apps</p>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="delivery-platform-links-body">
          Optional — add links to delivery apps you already use (Uber Eats, Deliveroo, Just Eat, Swiggy, and more). Customers tap a link and order directly in that app — those orders don't come through your BuildHof dashboard.
        </p>

        <div className="mt-3 flex flex-wrap gap-2" data-testid="delivery-platform-suggestions">
          {DELIVERY_PLATFORM_SUGGESTIONS.map((name) => {
            const alreadyAdded = usedNames.has(name.toLowerCase());
            return (
              <button
                key={name}
                type="button"
                disabled={alreadyAdded}
                data-testid={`delivery-platform-suggest-${name.toLowerCase().replace(/\s+/g, "-")}`}
                className="tap-target rounded-lg border bg-white px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-40"
                onClick={() => addPlatform(name)}
              >
                + {name}
              </button>
            );
          })}
        </div>

        <div className="mt-4 space-y-3" data-testid="delivery-platform-list">
          {platformLinks.map((item, index) => (
            <div key={item.id} className="rounded-lg border bg-white p-3" data-testid={`delivery-platform-row-${index}`}>
              <div className="grid gap-3 sm:grid-cols-[.4fr_1fr_auto]">
                <div>
                  <Label htmlFor={`delivery-platform-name-${item.id}`} className="text-xs font-bold" data-testid={`delivery-platform-name-${index}-label`}>App name</Label>
                  <Input
                    id={`delivery-platform-name-${item.id}`}
                    data-testid={`delivery-platform-name-${index}`}
                    className="mt-1 h-11 rounded-lg"
                    placeholder="e.g. Deliveroo"
                    value={item.name}
                    onChange={(event) => updatePlatform(item.id, { name: event.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor={`delivery-platform-url-${item.id}`} className="text-xs font-bold" data-testid={`delivery-platform-url-${index}-label`}>Store URL</Label>
                  <Input
                    id={`delivery-platform-url-${item.id}`}
                    data-testid={`delivery-platform-url-${index}`}
                    type="url"
                    className="mt-1 h-11 rounded-lg"
                    placeholder="https://..."
                    value={item.url}
                    onChange={(event) => updatePlatform(item.id, { url: event.target.value })}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 rounded-lg"
                    data-testid={`delivery-platform-remove-${index}`}
                    onClick={() => removePlatform(item.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {!platformLinks.length && (
            <p className="rounded-lg border border-dashed bg-white/70 p-4 text-sm text-muted-foreground" data-testid="delivery-platform-empty">
              No delivery apps added yet. Use a suggestion above or add a custom app.
            </p>
          )}
        </div>

        <Button type="button" variant="outline" className="mt-4 rounded-lg font-bold" data-testid="delivery-platform-add-button" onClick={() => addPlatform("")}>
          <Plus className="h-4 w-4" />
          Add delivery app
        </Button>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="delivery-platform-links-hint">
          Your BuildHof storefront checkout is separate — it uses the pickup and delivery settings above ({format(form.delivery_fee)} fee) and orders placed there do appear in your dashboard.
        </p>
      </section>
    </div>
  );
}
function PaymentsStep({ form, setForm, billingConfig, refresh }) {
  const { t } = useOnboardingI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connectError, setConnectError] = useState("");
  const stripeReady = Boolean(billingConfig?.stripe_configured || billingConfig?.connect_enabled);
  const status = ["not_started", "pending", "action_required", "connected"].includes(form.stripe_connect_status)
    ? form.stripe_connect_status
    : resolveStripeConnectStatus(form);
  const connectedEmail = String(form.stripe_connect_email || form.email || "").trim() || "Stripe account";

  const syncStatus = async ({ quiet = false } = {}) => {
    if (!stripeReady) return null;
    setSyncing(true);
    try {
      const result = await apiPost("/billing/connect/status", {});
      setForm((current) => applyConnectStatusToForm(current, result.connect, result.business));
      if (refresh) await refresh();
      return result;
    } catch (error) {
      if (!quiet) {
        toast.error(error.response?.data?.detail?.error?.message || "Could not refresh Stripe status");
      }
      return null;
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const connectFlag = searchParams.get("connect");
    if (!connectFlag) return undefined;
    let cancelled = false;
    (async () => {
      if (connectFlag === "oauth_error") {
        const message = searchParams.get("message") || "Could not link Stripe account";
        if (!cancelled) {
          setConnectError(message);
          toast.error(message);
        }
        const next = new URLSearchParams(searchParams);
        next.delete("connect");
        next.delete("business_id");
        next.delete("message");
        setSearchParams(next, { replace: true });
        return;
      }
      const result = await syncStatus({ quiet: true });
      if (cancelled) return;
      if (result?.connect?.stripe_connect_status === "connected") {
        toast.success("Stripe connected");
      } else if (connectFlag === "refresh") {
        toast.message("Continue Stripe setup — your previous link expired");
      } else if (result?.connect?.stripe_connect_status === "action_required") {
        toast.message("Stripe still needs a few more details");
      } else if (result?.connect?.stripe_account_id) {
        toast.message("Stripe is verifying your details");
      }
      const next = new URLSearchParams(searchParams);
      next.delete("connect");
      next.delete("business_id");
      next.delete("message");
      setSearchParams(next, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("connect")]);

  useEffect(() => {
    if (!stripeReady) return undefined;
    if (status === "not_started") return undefined;
    const timer = window.setInterval(() => {
      syncStatus({ quiet: true });
    }, 20000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeReady, status]);

  const connectStripe = async () => {
    if (!stripeReady) {
      toast.error("Payments are temporarily unavailable. Please try again shortly.");
      return;
    }
    setBusy(true);
    setConnectError("");
    try {
      const result = await apiPost("/billing/connect/start", {});
      setForm((current) => applyConnectStatusToForm(current, result.connect, result.business));
      if (result.url) {
        window.location.assign(result.url);
        return;
      }
      toast.error("Stripe did not return an onboarding URL");
    } catch (error) {
      const message = error.response?.data?.detail?.error?.message
        || (!error.response
          ? "Could not reach Stripe Connect. Platform admin must enable Connect at https://dashboard.stripe.com/connect, then retry."
          : "Could not start Stripe Connect");
      const code = error.response?.data?.detail?.error?.code || "";
      setConnectError(message);
      toast.error(message);
      if (code === "STRIPE_CONNECT_NOT_ENABLED" || /signed up for Connect/i.test(message)) {
        toast.message("Platform admin: enable Stripe Connect (Express) at dashboard.stripe.com/connect");
      }
    } finally {
      setBusy(false);
    }
  };

  const openStripeManage = async () => {
    if (!stripeReady) {
      toast.error("Stripe is not configured");
      return;
    }
    setBusy(true);
    try {
      const result = await apiPost("/billing/connect/dashboard-link", {});
      if (result.url) {
        window.location.assign(result.url);
        return;
      }
      toast.error("Could not open Stripe");
    } catch (error) {
      toast.error(error.response?.data?.detail?.error?.message || "Could not open Stripe dashboard");
    } finally {
      setBusy(false);
    }
  };

  const disconnectStripe = async () => {
    if (!window.confirm("Disconnect this Stripe account? Card checkout will pause until you connect again. You can link a different Stripe account afterward.")) {
      return;
    }
    setBusy(true);
    setConnectError("");
    try {
      const result = await apiPost("/billing/connect/disconnect", {});
      setForm((current) => applyConnectStatusToForm(current, result.connect, result.business));
      toast.success("Stripe disconnected — connect a new account when ready");
      if (refresh) await refresh();
    } catch (error) {
      const message = error.response?.data?.detail?.error?.message || "Could not disconnect Stripe";
      setConnectError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <StepIntro
        title={t("intro.payments.title")}
        body={t("intro.payments.body")}
      />

      {!stripeReady ? (
        <div
          className="mt-6 rounded-xl border border-[#E8C4A8] bg-[#FFF8F3] p-4"
          data-testid="payments-stripe-not-configured"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#D97A4A]" aria-hidden />
            <div>
              <p className="font-extrabold text-[#1A1A1A]">Payments are temporarily unavailable</p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                Stripe isn’t ready yet. Please try again shortly or contact support if this continues.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {connectError ? (
        <div
          className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-950"
          data-testid="payments-connect-error"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <div>
              <p className="font-extrabold">Could not connect Stripe</p>
              <p className="mt-1 text-sm leading-6">{connectError}</p>
              <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm leading-6">
                <li>
                  Platform admin: open{" "}
                  <a
                    href="https://dashboard.stripe.com/connect"
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold underline"
                    data-testid="payments-connect-enable-link"
                  >
                    Stripe Connect (live)
                  </a>
                  , turn Test mode off, and finish Get started / Enable Express.
                </li>
                <li>Come back to this page (Stripe will not redirect you here from that admin link).</li>
                <li>Click <span className="font-bold">Connect with Stripe</span> below — that flow returns you to BuildHof automatically.</li>
              </ol>
              <p className="mt-2 text-sm text-muted-foreground">
                Your platform secret key stays in the server env only. Owners never paste a secret key.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mt-6 space-y-3" data-testid="payments-connect-card">
        {status === "not_started" ? (
          <div className="rounded-2xl border border-[#E8E2D8] bg-white p-5 sm:p-6">
            <ul className="space-y-3 text-sm leading-6 text-[#4a493f]" data-testid="payments-benefits-list">
              <li className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3D7A4A]" aria-hidden />
                <span>Customers pay by card at checkout on your storefront</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3D7A4A]" aria-hidden />
                <span>Money goes directly to your bank account — we don’t hold your funds</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#3D7A4A]" aria-hidden />
                <span>No commission from us — Stripe’s own processing fee applies per transaction</span>
              </li>
            </ul>
            <Button
              type="button"
              className="tap-target mt-5 h-12 w-full rounded-xl text-base font-bold"
              data-testid="stripe-connect-button"
              disabled={!stripeReady || busy}
              onClick={connectStripe}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              Connect with Stripe
            </Button>
            <p className="mt-3 text-center text-sm text-muted-foreground">
              New to Stripe or already have a Stripe business — one setup. Payouts go to your bank.
            </p>
          </div>
        ) : null}

        {status === "pending" ? (
          <div className="rounded-xl border border-[#E8E2D8] bg-white p-4" data-testid="payments-status-pending">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-[#C4A574]" aria-hidden />
                <div>
                  <p className="font-extrabold text-[#1A1A1A]">Verifying your details</p>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    Stripe is confirming your information. This usually takes a few minutes, sometimes up to a day. You can keep setting up your storefront in the meantime.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-lg border-[#D8D2C6] bg-white"
                  data-testid="payments-refresh-status"
                  disabled={syncing}
                  onClick={() => syncStatus()}
                >
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Refresh status
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-lg border-[#D8D2C6] bg-white"
                  data-testid="payments-disconnect-pending-button"
                  disabled={busy}
                  onClick={disconnectStripe}
                >
                  Change Stripe account
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {status === "action_required" ? (
          <div className="rounded-xl border border-[#E8C4A8] bg-white p-4" data-testid="payments-status-action-required">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#D97A4A]" aria-hidden />
                <div>
                  <p className="font-extrabold text-[#1A1A1A]">Stripe needs more information</p>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    Add the remaining details Stripe requires before you can receive payouts.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                <Button
                  type="button"
                  className="rounded-lg font-bold"
                  data-testid="payments-continue-setup-button"
                  disabled={busy}
                  onClick={connectStripe}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Continue setup in Stripe
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-lg border-[#D8D2C6] bg-white"
                  data-testid="payments-disconnect-action-button"
                  disabled={busy}
                  onClick={disconnectStripe}
                >
                  Change Stripe account
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {status === "connected" ? (
          <div
            className="rounded-xl border border-[#C8DBC8] bg-[#F4F8F4] p-4"
            data-testid="payments-status-connected"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#3D7A4A] text-white">
                  <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
                </span>
                <div>
                  <p className="font-extrabold text-[#1A1A1A]">Stripe connected</p>
                  <p className="mt-1 text-sm text-muted-foreground">{connectedEmail}</p>
                  <p className="mt-1 text-sm font-medium text-[#3D7A4A]">
                    {form.stripe_connect_payouts_enabled ? "Payouts enabled" : "Charges enabled"}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-lg border-[#D8D2C6] bg-white"
                  data-testid="payments-manage-stripe-button"
                  disabled={busy}
                  onClick={openStripeManage}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Manage in Stripe
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-lg border-[#D8D2C6] bg-white"
                  data-testid="payments-disconnect-button"
                  disabled={busy}
                  onClick={disconnectStripe}
                >
                  Change Stripe account
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <p className="mt-4 text-sm text-muted-foreground">
        Your status updates automatically as Stripe processes your details — no need to refresh or check back manually.
      </p>
    </div>
  );
}

function SubscriptionStep({ form, setForm, business, currency, billingConfig, legalGaps }) {
  const { t } = useOnboardingI18n();
  const storeCurrency = FOUNDATION_PLAN.currency;
  const addons = normalizePlanAddons(form.plan_addons);
  const oneTime = normalizePlanOneTime(form.plan_one_time);
  const totals = calculatePlanTotals({ addons, oneTime, currency: storeCurrency });
  const paid = Boolean(form.subscription_active);
  const stripeReady = Boolean(billingConfig?.stripe_configured);
  const stripeLabel = "Stripe Checkout";
  const gaps = legalGaps || getLegalComplianceGaps({ legal_documents: form.legal_documents, country: form.country });

  useEffect(() => {
    if (!form.subscription_package_id) {
      setForm((current) => ({ ...current, subscription_package_id: FOUNDATION_PACKAGE_ID }));
    }
  }, [form.subscription_package_id, setForm]);

  const toggleMonthly = (id) => {
    if (paid) return;
    setForm({
      ...form,
      plan_addons: togglePlanSelection(addons, id),
      subscription_package_id: FOUNDATION_PACKAGE_ID,
    });
  };

  const toggleOneTime = (id) => {
    if (paid) return;
    setForm({
      ...form,
      plan_one_time: togglePlanSelection(oneTime, id),
      subscription_package_id: FOUNDATION_PACKAGE_ID,
    });
  };

  return (
    <div data-testid="onboarding-subscription-step">
      <StepIntro
        title={t("intro.subscription.title")}
        body={t("intro.subscription.body")}
      />

      {!paid && gaps.impressumMissing ? (
        <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950" data-testid="subscription-legal-hard-block">
          <p className="flex items-start gap-2 font-extrabold">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>Your Impressum is missing</span>
          </p>
          <p className="mt-2 text-sm leading-6">
            Businesses in Germany, Austria, and Switzerland are legally required to publish an Impressum. Add yours before going live.
          </p>
          <Link data-testid="subscription-add-impressum-link" to="/onboarding/legal">
            <Button type="button" className="mt-4 rounded-lg font-bold">
              Add Impressum
            </Button>
          </Link>
        </div>
      ) : null}

      {!paid && !gaps.impressumMissing && gaps.softMissing.length > 0 ? (
        <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950" data-testid="subscription-legal-soft-warn">
          <p className="flex items-start gap-2 font-extrabold">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>You&apos;re going live without some legal documents</span>
          </p>
          <p className="mt-2 text-sm leading-6">
            You haven&apos;t added: {gaps.softMissingLabels.join(", ")}. These protect both you and your customers. You can still go live, but please add them soon from Settings.
          </p>
          <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm font-semibold" data-testid="subscription-legal-soft-ack">
            <Checkbox
              checked={Boolean(form.legal_soft_ack)}
              onCheckedChange={(checked) => setForm({ ...form, legal_soft_ack: Boolean(checked) })}
              data-testid="subscription-legal-soft-ack-checkbox"
            />
            <span>I understand and will add these documents before accepting real orders.</span>
          </label>
          <Link data-testid="subscription-add-legal-link" to="/onboarding/legal" className="mt-3 inline-block text-sm font-bold text-[#173B2F] underline">
            Add legal documents now
          </Link>
        </div>
      ) : null}

      {paid ? (
        <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-900" data-testid="subscription-active-banner">
          Payment complete — Foundation is active
          {addons.length || oneTime.length ? " with your selected add-ons" : ""}. Continue to publish your storefront.
        </div>
      ) : null}

      <section className="mt-6 rounded-xl border bg-white p-5" data-testid="subscription-recurring-section">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Recurring monthly</p>
            <p className="mt-1 text-sm text-muted-foreground">Billed every month via Stripe Subscription. Cancel anytime after the 3-month minimum.</p>
          </div>
          <p className="text-xl font-extrabold text-[#173B2F]" data-testid="subscription-recurring-total">
            {totals.monthlyLabel}
            <span className="text-sm font-bold text-muted-foreground">/mo</span>
          </p>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-[#F8F5EE] px-4 py-3" data-testid="subscription-foundation-line">
          <div>
            <p className="font-extrabold">{FOUNDATION_PLAN.name}</p>
            <p className="text-xs text-muted-foreground">Included · 3-month minimum</p>
          </div>
          <p className="font-extrabold">
            {formatMoney(FOUNDATION_PLAN.price, storeCurrency)}
            <span className="text-xs font-bold text-muted-foreground">/mo + VAT</span>
          </p>
        </div>

        <p className="mt-5 text-xs font-bold uppercase tracking-wide text-muted-foreground">Monthly add-ons — add or remove</p>
        <div className="mt-3 space-y-3">
          {PLAN_ADDONS.filter((item) => !item.disabled).map((addon) => (
            <PlanAddonOffer
              key={addon.id}
              kind="monthly"
              id={addon.id}
              selected={addons.includes(addon.id)}
              disabled={paid}
              onToggle={() => toggleMonthly(addon.id)}
              testId={`subscription-addon-${addon.id}`}
            />
          ))}
        </div>
      </section>

      <section className="mt-4 rounded-xl border bg-white p-5" data-testid="subscription-one-time-section">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">One-time / prepaid</p>
            <p className="mt-1 text-sm text-muted-foreground">Charged once on checkout — not added to your monthly subscription.</p>
          </div>
          <p className="text-xl font-extrabold text-[#173B2F]" data-testid="subscription-one-time-total">
            {totals.oneTimeLabel}
          </p>
        </div>
        <div className="mt-3 space-y-3">
          {PLAN_ONE_TIME.map((item) => (
            <PlanAddonOffer
              key={item.id}
              kind="one_time"
              id={item.id}
              selected={oneTime.includes(item.id)}
              disabled={paid}
              onToggle={() => toggleOneTime(item.id)}
              testId={`subscription-one-time-${item.id}`}
            />
          ))}
        </div>
      </section>

      <div className="mt-4 rounded-xl border bg-white p-5" data-testid="subscription-checkout-summary">
        <PlanChargesSummary
          addons={addons}
          oneTime={oneTime}
          testId="subscription-charges-summary"
        />
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-[#F8F5EE] px-4 py-3" data-testid="subscription-recurring-card">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Recurring each month</p>
            <p className="mt-1 text-xl font-extrabold text-[#173B2F]">{totals.monthlyLabel}<span className="text-sm font-bold text-muted-foreground">/mo + VAT</span></p>
          </div>
          <div className="rounded-xl bg-[#F8F5EE] px-4 py-3" data-testid="subscription-due-today">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Due today (first invoice)</p>
            <p className="mt-1 text-xl font-extrabold text-[#173B2F]">{totals.dueTodayLabel}<span className="text-sm font-bold text-muted-foreground"> + VAT</span></p>
            <p className="mt-1 text-xs text-muted-foreground">
              {totals.monthlyLabel}/mo{totals.oneTimeTotal > 0 ? ` + ${totals.oneTimeLabel} one-time` : ""} · VAT calculated by Stripe Tax at checkout (e.g. DE 19%, PT 23%)
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border bg-white p-5" data-testid="subscription-payment-mode">
        <p className="text-sm font-extrabold text-[#173B2F]">Payment</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Secure card payment is handled by Stripe. You’ll complete checkout and return here to go live.
        </p>
        <div className="mt-3 rounded-lg bg-[#F8F5EE] px-4 py-3 text-sm text-[#173B2F]" data-testid="subscription-pay-go-live-copy">
          <p className="font-bold">
            You are subscribing to: BuildHof €99/month
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Payments are securely processed by Stripe on behalf of Build Up Rise Up UG (haftungsbeschränkt).
          </p>
        </div>
        {!stripeReady ? (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950" data-testid="subscription-stripe-missing">
            <p className="font-extrabold">Payments are temporarily unavailable</p>
            <p className="mt-1 text-sm">
              Stripe isn’t ready yet. Please try again shortly or contact support if this continues.
            </p>
          </div>
        ) : (
          <div className="mt-4 rounded-xl border border-[#173B2F] bg-[#F8F5EE] p-4" data-testid="subscription-pay-mode-stripe">
            <p className="font-extrabold">{stripeLabel}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              You’ll be redirected to Stripe-hosted checkout to pay securely with your card. Billing address and VAT ID (if any) are collected so Stripe Tax can apply the correct VAT.
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground" data-testid="subscription-payment-note">
        <CreditCard className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="font-bold text-[#173B2F]">Stripe Checkout</p>
          <p className="mt-1">
            Recurring items become a Subscription; one-time items are on the first invoice. After success you’re returned here and the store can go live.
          </p>
        </div>
      </div>
      {business?.subscription_paid_at && (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="subscription-paid-at">Paid at: {new Date(business.subscription_paid_at).toLocaleString()}</p>
      )}
      {currency && currency !== storeCurrency ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="subscription-currency-note">
          Plan billing is in {storeCurrency}. Your storefront prices use {currency}.
        </p>
      ) : null}
    </div>
  );
}
function DomainStep({ form, setForm, business, refresh }) {
  const { t } = useOnboardingI18n();
  const platformStoreUrl = `${PLATFORM_APP_HOST}/${form.slug || "your-store"}`;
  const customDomainPreview = form.desired_custom_domain
    ? form.desired_custom_domain.replace(/^https?:\/\//, "").replace(/\/$/, "")
    : "www.yourbusiness.com";
  const hasCustomDomain = Boolean(String(form.desired_custom_domain || "").trim());

  const setDomain = (value) => {
    const desired_custom_domain = value.replace(/^https?:\/\//, "").replace(/\/$/, "").toLowerCase();
    setForm({
      ...form,
      desired_custom_domain,
      domain_ownership_confirmed: desired_custom_domain ? form.domain_ownership_confirmed : false,
    });
  };

  return (
    <div data-testid="onboarding-domain-step">
      <StepIntro title={t("intro.domain.title")} body={t("intro.domain.body")} />

      <div className="mt-6">
        <Label htmlFor="store-slug-input" data-testid="store-slug-input-label">Store slug</Label>
        <Input
          id="store-slug-input"
          data-testid="store-slug-input"
          className="mt-2 h-12 rounded-lg"
          value={form.slug}
          onChange={(event) => setForm({ ...form, slug: slugify(event.target.value) })}
        />
        <p className="mt-3 flex items-center gap-2 text-sm font-bold text-[#4a7a5e]" data-testid="slug-availability-message">
          <Check className="h-4 w-4" />
          {platformStoreUrl} is available
        </p>
      </div>

      <section className="mt-6 rounded-xl bg-[#F2EFE4] p-5" data-testid="domain-option-project-local">
        <p className="font-extrabold text-[#173B2F]" data-testid="domain-option-1-title">
          <span className="mr-2 inline-block text-[#4a7a5e]" aria-hidden="true">●</span>
          Option 1 · Go live on your BuildHof link
        </p>
        <p className="mt-2 text-sm leading-6 text-muted-foreground" data-testid="domain-option-1-body">
          Your website can go live today at <span className="font-bold text-[#173B2F]">{platformStoreUrl}</span> — no extra setup needed. Connect your own domain anytime later.
        </p>
      </section>

      <section className="mt-4 rounded-xl bg-[#F2EFE4] p-5" data-testid="domain-option-custom">
        <p className="font-extrabold text-[#173B2F]" data-testid="domain-option-2-title">
          <span className="mr-2 inline-block text-[#EA6A3B]" aria-hidden="true">●</span>
          Option 2 · Use your own domain name
        </p>
        <p className="mt-2 text-sm leading-6 text-muted-foreground" data-testid="domain-option-2-body">
          If you already own a website address — like www.yourbusiness.com, bought through GoDaddy, Namecheap, or Cloudflare — we can connect it so customers visit your own address instead of ours.
        </p>
        <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm leading-6 text-[#4a493f]" data-testid="domain-option-2-steps">
          <li>Enter the domain you own below (usually starts with www).</li>
          <li>We&apos;ll give you a couple of short codes to add at your domain provider — we&apos;ll walk you through exactly where.</li>
          <li>Select <strong>Connect domain</strong>. Your site goes live on your own address once this is confirmed.</li>
        </ol>
        <div className="mt-4">
          <Label htmlFor="desired-custom-domain-input" data-testid="desired-custom-domain-input-label">Your domain</Label>
          <Input
            id="desired-custom-domain-input"
            data-testid="desired-custom-domain-input"
            className="mt-2 h-12 rounded-lg"
            placeholder="www.yourbusiness.com"
            value={form.desired_custom_domain}
            onChange={(event) => setDomain(event.target.value)}
          />
        </div>
        {hasCustomDomain ? (
          <details className="mt-4 rounded-lg border bg-white p-4" data-testid="domain-diy-dns-details">
            <summary className="cursor-pointer text-sm font-bold text-[#EA6A3B]">Prefer to add DNS records yourself now?</summary>
            <div className="mt-3">
              <CustomDomainPanel
                domain={form.desired_custom_domain}
                business={business}
                onVerified={() => refresh?.()}
              />
            </div>
          </details>
        ) : null}
      </section>

      <section className="mt-4 rounded-xl bg-[#F2EFE4] p-5" data-testid="domain-what-happens-next">
        <p className="font-extrabold text-[#173B2F]" data-testid="domain-next-title">
          <span className="mr-2 inline-block text-[#EA6A3B]" aria-hidden="true">●</span>
          What happens next
        </p>
        <p className="mt-2 text-sm leading-6 text-[#4a493f]" data-testid="domain-next-intro">
          Once you save your domain, our team takes it from here.
        </p>
        <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm leading-7 text-[#4a493f]" data-testid="domain-next-steps">
          <li>We start moving your website from your BuildHof link to your own domain.</li>
          <li>A team member handles the technical setup with you — no settings to figure out on your own.</li>
          <li>Your domain provider may send you a one-time code by email or text. Just share it with us when asked.</li>
          <li>Once verified, your site goes live on your own domain.</li>
        </ol>
        <div className="mt-4 rounded-lg bg-white p-4" data-testid="domain-mapping-example">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Example</p>
          <p className="mt-2 text-sm font-bold text-[#173B2F]">
            {platformStoreUrl} <span className="text-[#EA6A3B]">→</span>{" "}
            <span className="text-[#EA6A3B]">{customDomainPreview}</span>
          </p>
        </div>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="domain-next-timing">
          This usually takes a few business days, depending on your domain provider.
        </p>
      </section>

      <p className="mt-5 text-xs leading-6 text-muted-foreground" data-testid="domain-ownership-preamble">
        You&apos;re about to give us access to connect and host your website on this domain. Please make sure you&apos;re the owner of this domain, or have permission from the owner to manage it.
      </p>
      <label
        className={`mt-3 flex cursor-pointer items-start gap-3 text-sm leading-6 text-[#4a493f] ${hasCustomDomain ? "" : "opacity-70"}`}
        data-testid="domain-ownership-consent"
      >
        <Checkbox
          checked={Boolean(form.domain_ownership_confirmed)}
          disabled={!hasCustomDomain}
          onCheckedChange={(value) => setForm({ ...form, domain_ownership_confirmed: Boolean(value) })}
          data-testid="domain-ownership-checkbox"
          className="mt-0.5 h-4 w-4"
        />
        <span>
          I confirm that I own this domain (or am authorized to manage it on the owner&apos;s behalf), and I give BuildHof permission to use these details to connect and host my website.
        </span>
      </label>
    </div>
  );
}

function AgreementsStep({ form, setForm, onContinue }) {
  const { t } = useOnboardingI18n();
  const canContinue = Boolean(form.agreed_platform_agreements);
  const icons = {
    service_agreement: FileText,
    data_processing_agreement: ShieldCheck,
  };

  return (
    <div data-testid="onboarding-agreements-step">
      <StepIntro
        title={t("intro.agreements.title")}
        body={t("intro.agreements.body")}
      />
      <div className="mt-6 space-y-4">
        {PLATFORM_AGREEMENT_DOCS.map((item) => {
          const Icon = icons[item.id] || FileText;
          return (
            <article key={item.id} className="rounded-xl border bg-white p-4 sm:p-5" data-testid={`agreement-card-${item.id}`}>
              <div className="flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                <div className="flex min-w-0 items-start gap-3">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#173B2F]" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="font-extrabold" data-testid={`agreement-title-${item.id}`}>{item.title}</p>
                    <p className="mt-1 text-sm font-semibold text-[#173B2F]" data-testid={`agreement-price-${item.id}`}>
                      {item.price_label}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`agreement-effective-${item.id}`}>
                      {item.effective_label}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground" data-testid={`agreement-summary-${item.id}`}>
                      {item.summary}
                    </p>
                  </div>
                </div>
                <Link
                  to={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap self-start text-sm font-bold text-primary sm:justify-self-end"
                  data-testid={`agreement-view-${item.id}`}
                  onClick={() => logPlatformAgreementView(item.id, "onboarding_link")}
                >
                  View full text
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-6 space-y-4 rounded-xl border bg-white p-5">
        <label className="flex cursor-pointer items-start gap-3 text-sm font-semibold" data-testid="agreement-ack-bundle">
          <Checkbox
            checked={Boolean(form.agreed_platform_agreements)}
            onCheckedChange={(value) => setForm({ ...form, agreed_platform_agreements: Boolean(value) })}
            data-testid="agreement-checkbox-bundle"
            className="mt-0.5"
          />
          <span>
            I have read and agree to the BuildHof Service Agreement, including the Data Processing Agreement (Annex A).
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-3 text-sm font-semibold text-muted-foreground" data-testid="agreement-ack-marketing">
          <Checkbox
            checked={Boolean(form.marketing_consent)}
            onCheckedChange={(value) => setForm({ ...form, marketing_consent: Boolean(value) })}
            data-testid="agreement-checkbox-marketing"
            className="mt-0.5"
          />
          <span>
            Optional: send me product tips, feature updates, and occasional BuildHof news. You can unsubscribe anytime.
          </span>
        </label>
      </div>

      <Button
        type="button"
        className="tap-target mt-6 h-12 w-full rounded-xl text-base font-bold"
        data-testid="agreements-continue-button"
        disabled={!canContinue}
        onClick={onContinue}
      >
        {t("actions.continueToPayment")}
      </Button>
      <p className="mt-3 flex items-center justify-center gap-2 text-center text-sm text-muted-foreground" data-testid="agreements-record-note">
        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Acceptance is recorded with version, timestamp, and account details. Your accepted copy is available in Dashboard → Settings.
      </p>
    </div>
  );
}

function LegalStep({ form, setForm }) {
  const { t } = useOnboardingI18n();
  const uploadedCount = Object.keys(form.legal_documents || {}).length;
  const [footerPreviewOpen, setFooterPreviewOpen] = useState(false);
  const legalDocs = activeLegalDocuments(form.legal_documents);
  const address = formatBusinessAddress({
    line1: form.address_line1,
    line2: form.address_line2,
    city: form.city,
    postcode: form.postcode,
    country: form.country,
    show_on_storefront: form.show_address,
  });

  return (
    <div>
      <StepIntro
        title={t("intro.legal.title")}
        body={t("intro.legal.body")}
      />
      <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950" data-testid="legal-compliance-notice">
        <Shield className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <p data-testid="legal-compliance-notice-text">
          Please attach your <strong>privacy policy</strong>, <strong>terms &amp; conditions</strong>, <strong>cookie policy</strong>, <strong>Impressum</strong> (where required), and any other regional documents. BuildHof stores these for your storefront footer and compliance review.
        </p>
      </div>
      <LegalDocumentUploads
        documents={form.legal_documents}
        onChange={(legal_documents) => setForm({ ...form, legal_documents, legal_soft_ack: false })}
        testIdPrefix="onboarding-legal-doc"
      />
      <p className="mt-4 text-sm text-muted-foreground" data-testid="legal-upload-summary">
        {uploadedCount ? `${uploadedCount} document${uploadedCount === 1 ? "" : "s"} attached.` : "No documents uploaded yet — you can skip for now and add them before going fully live."}
      </p>

      <div className="mt-6 rounded-xl border border-[#cfd8c9] bg-[#eef3ea] p-5" data-testid="legal-footer-preview-card">
        <p className="flex items-center gap-2 text-sm font-extrabold text-[#2c2c2a]" data-testid="legal-footer-preview-title">
          <Eye className="h-4 w-4 text-[#4a7a5e]" aria-hidden="true" />
          Before you continue
        </p>
        <p className="mt-2 text-sm leading-6 text-[#4a493f]" data-testid="legal-footer-preview-body">
          The documents you upload here will appear as links in your website&apos;s footer, visible to every customer.
        </p>
        <Button
          type="button"
          variant="outline"
          className="mt-4 rounded-lg bg-white"
          data-testid="legal-footer-preview-button"
          onClick={() => setFooterPreviewOpen((open) => !open)}
        >
          {footerPreviewOpen ? "Hide footer preview" : "Preview footer →"}
        </Button>
        {footerPreviewOpen ? (
          <div className="mt-4 overflow-hidden rounded-xl bg-[#173B2F] p-4 text-white" data-testid="legal-footer-preview-panel">
            <div className="grid gap-4 text-sm md:grid-cols-3">
              <div>
                <p className="font-extrabold">{form.name || "Your business"}</p>
                <p className="mt-1 text-xs text-white/70 line-clamp-2">{form.description || "Your storefront tagline"}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-white/55">Contact</p>
                <p className="mt-1 text-white/85">{form.phone || "Phone"}</p>
                <p className="text-white/85">{form.email || "Email"}</p>
                <p className="text-white/85">{address || "Address"}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-white/55">Legal</p>
                <ul className="mt-1 space-y-1 font-bold">
                  {legalDocs.length ? (
                    legalDocs.map((doc) => <li key={doc.id}>{doc.label}</li>)
                  ) : (
                    <li className="font-normal text-white/55">Upload documents to see footer links</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
function PreviewStep({ form, selectedConfig, previewProducts, previewPhotos, previewMode, setPreviewMode, setForm, setHours, saveStep }) {
  const { t } = useOnboardingI18n();
  const { format, code: currencyCode } = useCurrency(form.currency);
  const updatePreviewProduct = (index, field, value) => {
    const products = [...previewProducts];
    products[index] = { ...products[index], [field]: field === "price" ? Number(value || 0) : value };
    setForm({ ...form, parsed_products: products });
  };

  return (
    <div>
      <StepIntro title={t("intro.preview.title")} body={t("intro.preview.body")} />
      <div className="mt-5 flex flex-wrap gap-2">
        <Button data-testid="preview-mobile-toggle" variant={previewMode === "mobile" ? "default" : "outline"} onClick={() => setPreviewMode("mobile")}>Mobile</Button>
        <Button data-testid="preview-desktop-toggle" variant={previewMode === "desktop" ? "default" : "outline"} onClick={() => setPreviewMode("desktop")}>Desktop</Button>
        <Button data-testid="preview-save-edits-button" variant="outline" onClick={() => saveStep("preview")}><Eye className="h-4 w-4" />Save edits</Button>
        <a href="#preview-menu-editor" data-testid="preview-edit-menu-link" className="tap-target rounded-lg border px-4 py-2 text-sm font-bold">Edit menu</a>
        <a href="#preview-hours-editor" data-testid="preview-edit-hours-link" className="tap-target rounded-lg border px-4 py-2 text-sm font-bold">Edit hours</a>
      </div>
      <div className={previewMode === "mobile" ? "phone-frame mx-auto mt-6 w-full max-w-[min(300px,calc(100vw-3rem))] overflow-hidden bg-white" : "mt-6 overflow-hidden rounded-2xl border bg-white"} data-testid="storefront-preview-frame">
        <img data-testid="preview-hero-image" src={previewPhotos[0]} alt="Preview" className="aspect-[4/3] w-full object-cover" />
        <div className="p-4">
          <Input data-testid="preview-hero-headline-input" value={form.hero_headline} onChange={(event) => setForm({ ...form, hero_headline: event.target.value })} className="font-bold" />
          <Textarea data-testid="preview-hero-subtext-input" value={form.hero_subtext} onChange={(event) => setForm({ ...form, hero_subtext: event.target.value })} className="mt-2" />
          <p className="mt-3 text-xs font-bold text-primary" data-testid="preview-config-label">{selectedConfig?.storefront_defaults?.hero_cta_text || "Start order"}</p>
          <div className="mt-4 grid gap-2" data-testid="preview-menu-list">
            {previewProducts.slice(0, 3).map((item, index) => (
              <div key={`${item.name}-${index}`} className="flex justify-between rounded-lg bg-[#F8F5EE] p-2 text-sm" data-testid={`preview-menu-item-${index}`}>
                <span>{item.name}</span>
                <span>{format(item.price)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <section id="preview-menu-editor" className="mt-6 rounded-lg border bg-white p-4" data-testid="preview-menu-editor">
        <h3 className="font-extrabold" data-testid="preview-menu-editor-title">Quick edit menu</h3>
        {previewProducts.slice(0, 4).map((item, index) => (
          <div key={`${item.name}-${index}`} className="mt-3 grid gap-2 md:grid-cols-[1fr_140px]" data-testid={`preview-menu-edit-row-${index}`}>
            <Input data-testid={`preview-menu-name-input-${index}`} value={item.name} onChange={(event) => updatePreviewProduct(index, "name", event.target.value)} />
            <PriceField compact testId={`preview-menu-price-input-${index}`} currency={currencyCode} value={String(item.price)} onChange={(value) => updatePreviewProduct(index, "price", value)} />
          </div>
        ))}
      </section>
      <section id="preview-hours-editor" className="mt-4 rounded-lg border bg-white p-4" data-testid="preview-hours-editor">
        <h3 className="font-extrabold" data-testid="preview-hours-editor-title">Quick edit hours</h3>
        <div className="mt-3">
          <HoursEditor hours={form.hours} onChange={setHours} compact testIdPrefix="preview-hours" />
        </div>
      </section>
    </div>
  );
}
function PublishStep({ form }) {
  const { t } = useOnboardingI18n();
  const storeUrl = `${window.location.origin}/${form.slug}`;

  const copyStoreLink = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(storeUrl);
      toast.success("Store link copied");
    } catch {
      window.prompt("Copy storefront link", storeUrl);
      toast.message("Copy link manually from the prompt");
    }
  };

  const downloadQr = async () => {
    try {
      const kit = await apiGet("/growth-kit");
      const svgPayload = kit.qr_svg?.replace(/^data:image\/svg\+xml;utf8,/, "") || "";
      const blob = new Blob([decodeURIComponent(svgPayload)], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${form.slug}-qr.svg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("QR code downloaded");
    } catch {
      toast.error("Could not download QR code");
    }
  };

  return (
    <div>
      <StepIntro title={t("intro.publish.title")} body={t("intro.publish.body")} />
      <div className="mt-6 rounded-lg border bg-[#EAF2E8] p-5">
        <p className="font-bold" data-testid="published-store-url">{PLATFORM_APP_HOST}/{form.slug}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link data-testid="view-storefront-after-publish-link" to={`/${form.slug}`}>
            <Button className="tap-target rounded-lg">View storefront</Button>
          </Link>
          <Button data-testid="copy-store-link-button" type="button" variant="outline" className="tap-target rounded-lg" onClick={copyStoreLink}>
            Copy link
          </Button>
          <Button data-testid="download-qr-button" type="button" variant="outline" className="tap-target rounded-lg" onClick={downloadQr}>
            Download QR
          </Button>
        </div>
      </div>
    </div>
  );
}
function ControlledField({ label, testId, value, onChange, maxLength }) {
  const length = String(value || "").length;
  return (
    <div>
      <div className="mb-2 flex items-end justify-between gap-2">
        <Label htmlFor={testId} data-testid={`${testId}-label`}>{label}</Label>
        {maxLength != null ? (
          <span className="text-xs tabular-nums text-muted-foreground" data-testid={`${testId}-count`}>
            {length}/{maxLength}
          </span>
        ) : null}
      </div>
      <Input
        id={testId}
        data-testid={testId}
        className="h-12 rounded-lg"
        value={value}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

const ONBOARDING_PLACEHOLDER_DESCRIPTIONS = new Set([
  "A welcoming local business ready to take online orders.",
  "A new local business powered by BuildHof.",
]);

const ONBOARDING_PLACEHOLDER_SUBTEXTS = new Set([
  "Order online for pickup or delivery.",
]);

function effectiveOnboardingDraft(text, placeholders) {
  const value = String(text || "").trim();
  if (!value || placeholders.has(value)) return "";
  return value;
}
function ToggleRow({ label, description, testId, checked, onChange, children = null }) {
  return (
    <div className="rounded-lg border p-4" data-testid={`${testId}-row`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="font-bold" data-testid={`${testId}-label`}>{label}</span>
          {description ? (
            <p className="mt-1 text-sm text-muted-foreground" data-testid={`${testId}-description`}>{description}</p>
          ) : null}
        </div>
        <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
      </div>
      {checked && children ? (
        <div className="mt-4 border-t border-[#E7DCC7] pt-4" data-testid={`${testId}-expanded`}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
